import React, { useState, useEffect, useRef } from 'react';
import { messagesForEvacuation } from '../helpers/messages';
import { logPodcastAction } from '../helpers/AnalyticsTags';
import { getDatabase, ref, onValue } from "firebase/database";
import { generatePodcastScript, geocodeAddress, getPathTimes, getTravelExperienceEnglishSummary } from '../helpers/AI_functions';
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { getFirestore, collection, getDocs, getDoc, doc } from 'firebase/firestore';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { haversineDistance, dijkstra, reconstructPath, formatTime, buildFreeFlowConnectorRoutes } from '../helpers/generalHelpers';
import { getTravelExperienceToDestinations, buildTravelNarrative, selectTwoDiverseRoutesSimple, getTravelExperiencesV2, selectTwoDiverseRoutes, buildTravelNarrativeV2 } from '../helpers/route_functions';
import HospitalIcon from "../assets/images/hospital.png";
import SafetyIcon from '../assets/images/safety_icon.png';
import LocationIcon from '../assets/images/user-location.png';
import { getHouseMarkerSize, getTrafficDotProfile } from './InteractiveMaps/mapZoomVisuals';
import FireImage from '../assets/images/fire_img.png'
import ParkingIconGreen from '../assets/images/parking_icon_green.svg';
import WUICastLogo from '../assets/images/wui_cast_logo_orange.png';
import BalloonsOverlay from './FunScreens/BalloonsOverlay';
import RainOverlay from './FunScreens/RainOverlay';
import HeroFrame from './PublicPodcastScreens/HeroFrame';
import HeroSceneMedia from './PublicPodcastScreens/HeroSceneMedia';
import useHeroSceneMedia from './PublicPodcastScreens/useHeroSceneMedia';
import { getUiText } from '../helpers/localizationHelpers';
import { width } from '@amcharts/amcharts4/.internal/core/utils/Utils';
import { normalizeScenes, SCENE_SYMBOLS, routeChecklistItems, 
  extractStreetFromInput,
  streetSimilarity,
  linkStreetName, 
  parseDurationToSeconds,
  normalizePolygonPath,
  getSmokeOverlayKey,
  createSmokeEmberOverlay} from '../helpers/podcastHelpers';
import {
  resolveNextStepsRuntimeConfig,
  stripNextStepsFaqSystemFields
} from '../helpers/nextStepsFaqHelpers';
import { pickNearestGraphNode } from '../helpers/podcastRouteHelpers';
import { useParams } from "react-router-dom";

  
import { darkMapStyle, HOSPITAL_LOCATION, LANGUAGES } from '../helpers/mapHelpers';
import SearchBar from '../components/SearchBar';
import "./InteractiveMapUI.css"
import useRealtimeNextStepsAssistant from '../hooks/useRealtimeNextStepsAssistant';
import useScreenWakeLock from '../hooks/useScreenWakeLock';
import useNextStepAudio from '../hooks/useNextStepAudio';
import NextStepsModal from './InteractiveMaps/components/NextStepsModal';
import useDisclaimerGate, { useDisclaimerAudioUrl } from '../hooks/useDisclaimerGate';
import DemoLeadGateModal from './PublicPodcastScreens/DemoLeadGateModal';
import { PodcastCaptionOverlay, PodcastCcButton } from './PublicPodcastScreens/PodcastCaptions';
import usePodcastCaptions from '../hooks/usePodcastCaptions';
import useInteractiveMapDiagnostics from '../hooks/useInteractiveMapDiagnostics';

// Replace with your actual API key.
const GOOGLE_MAPS_API_KEY = 'AIzaSyASU8wb6swDIo3dNxkyjfraWijCImc3-OU';
const GOOGLE_MAP_LIBRARIES = ["places"];
const ENABLE_NEXT_STEPS_VOICE_ASSISTANT = false;
const INTERACTIVE_MAP_FEATURES = {
  showEvacuationTimesTab: false
};
const NEXT_STEPS_AUTO_SHOW_SECONDS = 45;
const NEXT_STEPS_ANALYTICS_SECONDS = 90;
const DEFAULT_PARTNER_LOGO_TIERS = [
  { key: "main_partners", name: "Main partners", rank: 1 },
  { key: "supporting_partners", name: "Supporting partners", rank: 2 },
  { key: "other_partners", name: "Other partners", rank: 3 }
];
const PARTNER_LOGO_TIER_KEYS = new Set(DEFAULT_PARTNER_LOGO_TIERS.map((tier) => tier.key));

const readSessionFlag = (key) => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === "true";
  } catch (_) {
    return false;
  }
};

const writeSessionFlag = (key) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, "true");
  } catch (_) {
    // Storage can be unavailable in privacy-restricted browsers; the in-memory unlock still applies.
  }
};

const storageErrorName = (error) => String(error?.code || error?.name || "unknown_error").replace(/[^a-zA-Z0-9_-]/g, "_");

const fetchStorageText = async (path, onFailure, maxAttempts = 3) => {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const url = await getDownloadURL(storageRef(getStorage(), path));
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error(`HTTP_${response.status}`);
      return { text: await response.text(), attempt };
    } catch (error) {
      lastError = error;
      onFailure?.(error, attempt, maxAttempts);
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 500 * (2 ** (attempt - 1))));
      }
    }
  }
  throw lastError;
};

const normalizePartnerLogoTierKey = (logoOrTier) => {
  const raw = typeof logoOrTier === "string"
    ? logoOrTier
    : (logoOrTier?.tierKey || logoOrTier?.tier || "");
  if (PARTNER_LOGO_TIER_KEYS.has(raw)) return raw;
  if (raw === "lead") return "main_partners";
  if (raw === "supporting") return "supporting_partners";
  return "supporting_partners";
};

const normalizePartnerLogoTiers = (rawTiers) => {
  const byKey = new Map(
    (Array.isArray(rawTiers) ? rawTiers : [])
      .filter((tier) => PARTNER_LOGO_TIER_KEYS.has(tier?.key))
      .map((tier) => [tier.key, tier])
  );

  return DEFAULT_PARTNER_LOGO_TIERS.map((tier) => {
    const saved = byKey.get(tier.key);
    return {
      ...tier,
      name: String(saved?.name || tier.name).trim() || tier.name
    };
  });
};

const inferRouteMediaKind = (metadata = {}) => {
  const explicit = String(metadata.mediaType || metadata.kind || "").toLowerCase();
  if (explicit === "video" || explicit.startsWith("video/")) return "video";
  if (explicit === "audio" || explicit.startsWith("audio/")) return "audio";

  const contentType = String(metadata.contentType || "").toLowerCase();
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("audio/")) return "audio";

  const path = String(metadata.objectPath || metadata.gsUri || metadata.fileName || "").toLowerCase();
  if (path.endsWith(".mp4") || path.endsWith(".mov") || path.endsWith(".webm")) return "video";
  return "audio";
};

const firestore = getFirestore();

const containerStyle = {
  width: '100vw',
  height: '100vh'
};

const InteractiveMapMain = ({ podcastIdOverride = "", demoGate = null }) => {
  // Existing states
  const initialLang =
   (typeof window !== "undefined" &&
     (new URLSearchParams(window.location.search).get("lang") || "")
   ).toUpperCase() || 'EN';
  const [selectedLanguage, setSelectedLanguage] = useState(initialLang);
  const { podcastId: routePodcastId } = useParams();
  const podcastId = podcastIdOverride || routePodcastId;
  const demoGateEnabled = Boolean(demoGate);
  const optionalPromptAfter = Number(demoGate?.optionalPromptAfter ?? 40);
  const requiredPromptAt = Number(demoGate?.requiredPromptAt ?? 72);

  
  const [selectedTab, setSelectedTab] = useState('routes');
  const [podcastStream, setPodcastStream] = useState("");
  const [podcastUploadPoint, setPodcastUploadPoint] = useState(null);
  const [podcastAudioUrl, setPodcastAudioUrl] = useState("");
  const [checkedItems, setCheckedItems] = useState({});
  const [travelExperiences, setTravelExperiences] = useState(false);
  const [isPodcastPlaying, setIsPodcastPlaying] = useState(false);
  const [travelExpNarrativeStream, setTravelExpNarrativeStream] = useState("");
  const mapRef = useRef(null);
  const podcastAudioRef = useRef(null);
  const safetyCalloutsRef = useRef([]);
  const [routesAudioUrl, setRoutesAudioUrl] = useState("");
  const [routesMediaKind, setRoutesMediaKind] = useState("audio");
  const routesAudioRef = useRef(null);
  const disclaimerAudioUrl = useDisclaimerAudioUrl(selectedLanguage);
  const {
    disclaimerAudioRef,
    disclaimerPhase,
    isDisclaimerPlaying,
    playWithDisclaimer,
    pauseActivePlayback,
    handleDisclaimerEnded,
    resetDisclaimer,
    gateMainPlayback,
  } = useDisclaimerGate(routesAudioRef);
  const routesDataRef = useRef([]);   // ←  NEW
  const activeRouteNodeRef = useRef(null);   // remembers last highlight
  const [destinationNodes, setDestinationNodes] = useState([]);
  const [isMobile, setIsMobile] = useState(false)
  const prefillDoneRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const wantRoutesAutoplayRef = useRef(false);
  const routesAutoplayInFlightRef = useRef(false);
  const [showNextSteps, setShowNextSteps] = useState(false);
  const [activeStepsTab, setActiveStepsTab] = useState('next');
  const [routeScenes, setRouteScenes] = useState([]);
  const [routeTimings, setRouteTimings] = useState({});
  const showRouteTimesWhenPausedRef = useRef(true);
  const [simulationPdfUrl, setSimulationPdfUrl] = useState("");
  const [podcastPolygons, setPodcastPolygons] = useState({});
  const rawScenesRef = useRef([]);
  const hasLoggedPlayRef   = useRef(false);
  const hasLoggedAddressTypingStartedRef = useRef(false);
  const nearestNodeIdRef   = useRef(null);
  const [selectedNearestNodeId, setSelectedNearestNodeId] = useState("");
  const captions = usePodcastCaptions({
    podcastId,
    languageId: selectedLanguage,
    nodeId: selectedNearestNodeId,
    mainMediaRef: routesAudioRef,
    disclaimerAudioRef,
    isDisclaimerPlaying
  });
  const lateQueuesRef = useRef(new Map());
  const additionalHouseholdNodesRef = useRef([]);
  const additionalHouseMarkersRef = useRef([]);

  useEffect(() => {
    if (disclaimerPhase === "playing") setIsPodcastPlaying(true);
    if (disclaimerPhase === "paused" || disclaimerPhase === "awaiting-main-play") {
      setIsPodcastPlaying(false);
    }
  }, [disclaimerPhase]);

  // Map section states
  const [time, setTime] = useState(180);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [simulations, setSimulations] = useState([]);
  const [selectedSimulation, setSelectedSimulation] = useState();
  const [showPolyLines, setShowPolyLines] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);
  // Graph data state
  const [graphData, setGraphData] = useState({ links: [], nodes: {}, adjList: {} });
  const [simulationDataRetryKey, setSimulationDataRetryKey] = useState(0);
  const [simulationDataLoading, setSimulationDataLoading] = useState({ links: false, nodes: false });
  const [simulationDataFailures, setSimulationDataFailures] = useState({ links: "", nodes: "" });
  // New state for car markers (if using Markers)
  const carMarkersRef = useRef([]);
  // We'll continue to use the existing polylineRefs.
  const polylineRefs = useRef([]);
  const circleRef = useRef(null);
  const hasCrossedNextStepsThresholdRef = useRef(false);
  const hasCrossed90Ref = useRef(false);
  const nextStepsShownRef = useRef(false);

  // New state to hold car overlays (if switching to OverlayView, see previous example)
  const [carOverlays, setCarOverlays] = useState([]);
  const [CalloutOverlayClass, setCalloutOverlayClass] = useState(null);

  const [searchText,      setSearchText]      = useState("");   // text in the bar
  const [isSearchingLoc,  setIsSearchingLoc]  = useState(false); // spinner flag

  // Storyboard overlay refs
const linkGeomsRef = useRef({});                // link_id -> WKT geometry
const storyboardLinkPolysRef = useRef(new Map()); // linkId -> google.maps.Polyline
const storyboardNodeMarkersRef = useRef(new Map()); // nodeId -> google.maps.Marker
const storyboardFirePolysRef = useRef([]); // Fire polygon ref
const storyboardSmokeOverlaysRef = useRef(new Map()); // stable smoke key -> OverlayView
const lastStoryboardCameraRef = useRef(null);   // {lat,lng,zoom}
const [activeCallout, setActiveCallout] = useState(null); // { text, bg, textColor } | null
const [balloonsSpec, setBalloonsSpec] = useState(null);
const sceneTimeOverlaysRef = useRef([]); // overlays created from scene.destTimes
const [routeVideoHiddenForCamera, setRouteVideoHiddenForCamera] = useState(false);
const routeVideoCameraTimerRef = useRef(null);
const [isMapShaking, setIsMapShaking] = useState(false);
const [currentMapTypeId, setCurrentMapTypeId] = useState("roadmap");

const isEnglish = selectedLanguage === 'EN';

const attachSavedPolygons = React.useCallback(
  (scenes) => {
    if (!Array.isArray(scenes)) return [];
    return scenes.map((scene) => {
      if (!Array.isArray(scene.firePolygons) || !scene.firePolygons.length) return scene;
      const resolved = scene.firePolygons
        .map((fp) => {
          if (!fp) return fp;
          if (fp.polygon_name) {
            const key = String(fp.polygon_name);
            const saved = podcastPolygons[key];
            if (saved?.path?.length) {
              return {
                ...fp,
                path: saved.path
              };
            }
          }
          return fp;
        })
        .filter((fp) => Array.isArray(fp?.path) && fp.path.length >= 3);

      const changed =
        resolved.length !== scene.firePolygons.length ||
        resolved.some((fp, idx) => fp !== scene.firePolygons[idx]);

      if (!changed) return scene;
      return { ...scene, firePolygons: resolved };
    });
  },
  [podcastPolygons]
);


const [checklistItems, setChecklistItems] = useState([]);
const activeChecklistItems = React.useMemo(
  () => (checklistItems?.length ? checklistItems : routeChecklistItems), // fallback ok
  [checklistItems]
);

const hospitalMarkerRef = useRef(null);

const [deviceZoom, setDeviceZoom] = useState(
  (typeof window !== "undefined" && window.innerWidth <= 768) ? 14 : 16
);

const uiText = getUiText(selectedLanguage);
const addressFlowText = uiText.addressEntryFlow || {};
const addressFlowErrors = addressFlowText.errors || {};
const addressProgressSteps =
  Array.isArray(addressFlowText.progressSteps) && addressFlowText.progressSteps.length
    ? addressFlowText.progressSteps
    : [
        "Finding your address",
        "Loading simulation data",
        "Preparing your guide",
        "Loading audio"
      ];

const [activeNodes, setActiveNodes] = useState([]);
const [activeNodesForLanguage, setActiveNodesForLanguage] = useState("");
const podcastNodeIds = React.useMemo(() => {
    return new Set((activeNodes || []).map(String));
  }, [activeNodes]);

const onTimeQueuesByDestRef = useRef(new Map()); // destId -> Map(linkId -> queuedVehicles)
const onTimeTotalsByDestRef = useRef(new Map()); // destId -> total seconds (leaveOnTime)

const isSeekingRef = useRef(false);
const pauseTimerRef = useRef(null);
const lastAudioSrcRef = useRef('');
const demoSubmissionKey = `wui_cast_demo_submitted:${podcastId}`;
const [demoSubmitted, setDemoSubmitted] = useState(() => (
  demoGateEnabled && readSessionFlag(demoSubmissionKey)
));
const demoSubmittedRef = useRef(demoSubmitted);
const demoSoftPromptShownRef = useRef(false);
const demoRequiredGateOpenRef = useRef(false);
const [demoPrompt, setDemoPrompt] = useState(null);
const [demoPromptTime, setDemoPromptTime] = useState(0);

const [routeTableRows, setRouteTableRows] = useState([]);
const [routeTableLoading, setRouteTableLoading] = useState(false);
const [routeTableError, setRouteTableError] = useState('');
const [canShowNextStepsForSelectedNode, setCanShowNextStepsForSelectedNode] = useState(false);

const [openInstructionKey, setOpenInstructionKey] = useState(null);
const [activeNextStepIndex, setActiveNextStepIndex] = useState(0);
const [completedNextStepKeys, setCompletedNextStepKeys] = useState([]);
const [hiddenNextStepKeys, setHiddenNextStepKeys] = useState([]);
const nextStepsVoiceAssistant = useRealtimeNextStepsAssistant({
  isModalOpen: showNextSteps,
  workflow: "cws_registration"
});
useScreenWakeLock(isPodcastPlaying);

const [showAddressEntryFlow, setShowAddressEntryFlow] = useState(!demoGateEnabled);
const [addressEntryMode, setAddressEntryMode] = useState("form");
const [addressEntryError, setAddressEntryError] = useState("");
const [isPickOnMapMode, setIsPickOnMapMode] = useState(false);
const [addressSuggestions, setAddressSuggestions] = useState([]);
const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
const [resolvedAddressLabel, setResolvedAddressLabel] = useState("");
const autocompleteServiceRef = useRef(null);
const predictionRequestSeqRef = useRef(0);

const FALLBACK_LOCATION = { lat: 37.111, lng: -122.1111 };
const ORIGINAL_DEFAULT_ZOOM = { desktop: 16, mobile: 14 };

const [defaultLocation, setDefaultLocation] = useState(FALLBACK_LOCATION);
const [defaultZoom, setDefaultZoom] = useState(() =>
  (typeof window !== "undefined" && window.innerWidth <= 768)
    ? ORIGINAL_DEFAULT_ZOOM.mobile
    : ORIGINAL_DEFAULT_ZOOM.desktop
);
const [partnerLogos, setPartnerLogos] = useState([]);
const [partnerLogoTiers, setPartnerLogoTiers] = useState(DEFAULT_PARTNER_LOGO_TIERS);
const [mapCenter, setMapCenter] = useState(null);
const [podcastConfig, setPodcastConfig] = useState(null);
const [podcastConfigLoaded, setPodcastConfigLoaded] = useState(false);
const [podcastTitle, setPodcastTitle] = useState("");
const [podcastCta, setPodcastCta] = useState("");
const isSingleLocation = podcastConfig?.location_mode === "single";
const [nextStepsFaqConfig, setNextStepsFaqConfig] = useState(null);
const [nextStepsFaqLoading, setNextStepsFaqLoading] = useState(false);
const [activeOverlays, setActiveOverlays] = useState([]);
const [sceneFlooding, setSceneFlooding] = useState(false);
const {
  activeHeroUrl,
  activeHeroVideo,
  heroAnim,
  heroVideoRef,
  heroVisible,
  applyHeroMediaForScene,
  clearHeroMedia,
  handleHeroAnimationEnd
} = useHeroSceneMedia({ podcastConfig, isPodcastPlaying });
const partnerLogoSections = React.useMemo(
  () =>
    partnerLogoTiers
      .slice()
      .sort((a, b) => Number(a.rank || 0) - Number(b.rank || 0))
      .map((tier) => ({
        ...tier,
        logos: partnerLogos.filter((logo) => normalizePartnerLogoTierKey(logo) === tier.key)
      }))
      .filter((section) => section.logos.length > 0),
  [partnerLogoTiers, partnerLogos]
);

const normalizeLatLng = (loc) => {
    if (!loc) return null;
    if (typeof loc.lat === "number" && typeof loc.lng === "number") return { lat: loc.lat, lng: loc.lng };
    if (typeof loc.latitude === "number" && typeof loc.longitude === "number") return { lat: loc.latitude, lng: loc.longitude };
    return null;
};

const pickDefaultZoom = (dz) => {
  // support number OR { desktop, mobile }
  if (Number.isFinite(dz)) return dz;

  if (dz && typeof dz === "object") {
    const mobile  = Number(dz.mobile);
    const desktop = Number(dz.desktop);
    const isMob = (typeof window !== "undefined" && window.innerWidth <= 768);

    const preferred = isMob ? mobile : desktop;
    if (Number.isFinite(preferred)) return preferred;

    // fallback to whichever exists
    if (Number.isFinite(desktop)) return desktop;
    if (Number.isFinite(mobile)) return mobile;
  }

  return null; // means "not present / invalid"
};



const searchLoadingMessages = addressProgressSteps;
const [searchLoadingStep, setSearchLoadingStep] = useState(0);
const [autoplayOutcome, setAutoplayOutcome] = useState("pending");
const autoplayOutcomeRef = useRef("pending");
const {
  logRoutePlaybackDiagnostic,
  logRouteMediaDiagnostic,
  resetMediaDiagnostics
} = useInteractiveMapDiagnostics({
  isMobile,
  selectedLanguage,
  podcastId,
  selectedSimulation,
  nearestNodeIdRef,
  routesMediaKind
});


useEffect(() => {
    if (!podcastId) {
      setPodcastConfig({});
      setPodcastConfigLoaded(true);
      return;
    }
  
    const db = getFirestore();
    setPodcastConfigLoaded(false);
  
    (async () => {
      try {
        const snap = await getDoc(doc(db, "podcasts", String(podcastId)));
        if (!snap.exists()) {
          setPodcastConfig({});
          return;
        }
  
        const cfg = snap.data() || {};
        setPodcastConfig(cfg);
  
        const dl = normalizeLatLng(cfg.default_location);
        if (dl) {
          setDefaultLocation(dl);
          setMapCenter(dl);
  
        }

        const dz = pickDefaultZoom(cfg.default_zoom);
        if (dz != null) {
          setDefaultZoom(dz);
        }

        const storage = getStorage();
        setPartnerLogoTiers(normalizePartnerLogoTiers(cfg.partner_logo_tiers));
        const rawPartnerLogos = Array.isArray(cfg.partner_logos) ? cfg.partner_logos : [];
        const resolvedPartnerLogos = await Promise.all(rawPartnerLogos.map(async (logo) => {
          const storagePath = String(logo?.storagePath || "");
          const url = storagePath
            ? await getDownloadURL(storageRef(storage, storagePath)).catch(() => logo?.url || "")
            : (logo?.url || "");
          const tierKey = normalizePartnerLogoTierKey(logo);
          return { ...logo, tierKey, url };
        }));
        setPartnerLogos(resolvedPartnerLogos.filter((logo) => logo?.url));

        if (Array.isArray(cfg.overlay)) {
          const normalized = cfg.overlay
            .map((item) => {
              if (typeof item === "string") return item;
              if (item == null) return "";
              return String(item);
            })
            .map((item) => item.trim().toLowerCase())
            .filter(Boolean);
          setActiveOverlays(normalized);
        } else {
          setActiveOverlays([]);
        }

      } catch (e) {
        console.error("Failed to load podcast config:", e);
        setPodcastConfig({});
      } finally {
        setPodcastConfigLoaded(true);
      }
    })();
  }, [podcastId]);

useEffect(() => {
  if (!podcastId) {
    setNextStepsFaqConfig(null);
    setNextStepsFaqLoading(false);
    return;
  }

  let cancelled = false;
  const db = getFirestore();
  const langCode = String(selectedLanguage || "EN").toUpperCase();

  const loadConfig = async (code) => {
    const snap = await getDoc(doc(db, "podcasts", String(podcastId), "next-steps-faq", code));
    if (!snap.exists()) return null;
    return stripNextStepsFaqSystemFields(snap.data() || {});
  };

  (async () => {
    try {
      setNextStepsFaqLoading(true);
      const config = await loadConfig(langCode);
      const fallbackConfig = !config && langCode !== "EN" ? await loadConfig("EN") : null;
      if (!cancelled) setNextStepsFaqConfig(config || fallbackConfig || null);
    } catch (e) {
      console.error("Failed to load next steps/FAQ config:", e);
      if (!cancelled) setNextStepsFaqConfig(null);
    } finally {
      if (!cancelled) setNextStepsFaqLoading(false);
    }
  })();

  return () => {
    cancelled = true;
  };
}, [podcastId, selectedLanguage]);

useEffect(() => {
  if (!podcastId) {
    setActiveNodes([]);
    setActiveNodesForLanguage("");
    setPodcastTitle("");
    setPodcastCta("");
    return;
  }

  const db = getFirestore();
  const langCode = String(selectedLanguage || "EN").toUpperCase();
  let cancelled = false;
  setActiveNodes([]);
  setActiveNodesForLanguage("");
  setPodcastTitle("");
  setPodcastCta("");
  
    const loadForLang = async (code, includeTitle = false) => {
      const snap = await getDoc(doc(db, "podcasts", String(podcastId), "general_details", code));
      if (!snap.exists()) return false;

      const data = snap.data() || {};
      if (!cancelled) {
        setActiveNodes(Array.isArray(data.active_nodes) ? data.active_nodes.map(String) : []);
        if (includeTitle) {
          setPodcastTitle(String(data.title || "").trim());
          setPodcastCta(String(data.cta_label || "").trim());
        }
      }
      return true;
    };
  
    (async () => {
      const ok = await loadForLang(langCode, true);
      if (!ok && langCode !== "EN") await loadForLang("EN"); // optional fallback
      if (!cancelled) setActiveNodesForLanguage(langCode);
    })();

    return () => {
      cancelled = true;
    };
  }, [podcastId, selectedLanguage]);

useEffect(() => {
  const pid = String(podcastId || "").trim();
  const nid = String(selectedNearestNodeId || "").trim();
  const langCode = String(selectedLanguage || "EN").toUpperCase();

  if (!pid || !nid || !langCode) {
    setSimulationPdfUrl("");
    return;
  }

  let cancelled = false;
  const db = getFirestore();
  const storage = getStorage();

  (async () => {
    try {
      const metaSnap = await getDoc(
        doc(db, "podcasts", pid, "podcast_details", nid, langCode, "podcastMetadata")
      );
      const pdfCreated = metaSnap.exists() && metaSnap.data()?.pdfCreated === true;
      if (!pdfCreated) {
        if (!cancelled) setSimulationPdfUrl("");
        return;
      }

      const url = await getDownloadURL(
        storageRef(storage, `Podcasts/${pid}/${langCode}/${nid}/evac_times.pdf`)
      );
      if (!cancelled) setSimulationPdfUrl(url);
    } catch (err) {
      console.error("Failed to load simulation PDF:", err);
      if (!cancelled) setSimulationPdfUrl("");
    }
  })();

  return () => {
    cancelled = true;
  };
}, [podcastId, selectedLanguage, selectedNearestNodeId]);

useEffect(() => {
  if (!podcastId) {
    setPodcastPolygons({});
    return;
  }

  let cancelled = false;
  const db = getFirestore();
  const storage = getStorage();

  const loadPolygons = async () => {
    try {
      const snap = await getDocs(collection(db, "podcasts", String(podcastId), "polygons"));
      const entries = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const data = docSnap.data() || {};
          const key = docSnap.id;
          const storagePath = data.storagePath || `Podcasts/${podcastId}/polygons/${key}.json`;

          try {
            const url = await getDownloadURL(storageRef(storage, storagePath));
            const resp = await fetch(url);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const json = await resp.json();
            const path = normalizePolygonPath(json);
            return [key, { ...data, path }];
          } catch (err) {
            console.error("Failed to download polygon", key, err);
            return [key, { ...data, path: [] }];
          }
        })
      );

      if (cancelled) return;

      const map = {};
      entries.forEach(([key, value]) => {
        map[key] = value;
      });
      setPodcastPolygons(map);
    } catch (err) {
      console.error("Failed to fetch polygon metadata:", err);
      if (!cancelled) setPodcastPolygons({});
    }
  };

  loadPolygons();
  return () => {
    cancelled = true;
  };
}, [podcastId]);

useEffect(() => {
  let intervalId = null;

  if (isSearchingLoc) {
    setSearchLoadingStep(0);

    intervalId = setInterval(() => {
      setSearchLoadingStep(prev => {
        if (prev >= searchLoadingMessages.length - 2) {
          clearInterval(intervalId);
          return prev; // stay at "Loading podcast"
        }
        return prev + 1;
      });
    }, 3000);
  } else {
    setSearchLoadingStep(0);
  }

  return () => {
    if (intervalId) clearInterval(intervalId);
  };
}, [isSearchingLoc, searchLoadingMessages.length]);

useEffect(() => {
  if (autoplayOutcomeRef.current === "blocked") return;
  if (selectedLocation && !isSearchingLoc && (addressEntryMode === "loading" || addressEntryMode === "ready")) {
    setShowAddressEntryFlow(false);
    setAddressEntryMode("form");
    setAddressEntryError("");
    setIsPickOnMapMode(false);
  }
}, [selectedLocation, isSearchingLoc, addressEntryMode]);

useEffect(() => {
  if (!showAddressEntryFlow || addressEntryMode !== "form" || isSearchingLoc) {
    setAddressSuggestions([]);
    setIsFetchingSuggestions(false);
    return;
  }

  const input = searchText.trim();
  if (input.length < 2) {
    setAddressSuggestions([]);
    setIsFetchingSuggestions(false);
    return;
  }

  if (!window.google?.maps?.places?.AutocompleteService) {
    setAddressSuggestions([]);
    setIsFetchingSuggestions(false);
    return;
  }

  if (!autocompleteServiceRef.current) {
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
  }

  const reqId = ++predictionRequestSeqRef.current;
  const timerId = setTimeout(() => {
    setIsFetchingSuggestions(true);
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        types: ["geocode"],
        componentRestrictions: { country: "us" }
      },
      (predictions, status) => {
        if (reqId !== predictionRequestSeqRef.current) return;

        const ok = status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(predictions);
        if (!ok) {
          setAddressSuggestions([]);
          setIsFetchingSuggestions(false);
          return;
        }

        setAddressSuggestions(
          predictions.slice(0, 6).map((prediction) => ({
            id: prediction.place_id || prediction.description,
            value: prediction.description,
            title: prediction.structured_formatting?.main_text || prediction.description,
            subtitle: prediction.structured_formatting?.secondary_text || ""
          }))
        );
        setIsFetchingSuggestions(false);
      }
    );
  }, 220);

  return () => clearTimeout(timerId);
}, [showAddressEntryFlow, addressEntryMode, isSearchingLoc, searchText]);



const clearPauseTimer = () => {
  if (pauseTimerRef.current) {
    clearTimeout(pauseTimerRef.current);
    pauseTimerRef.current = null;
  }
};

const openDemoPrompt = (mode, playbackTime) => {
  if (!demoGateEnabled || demoSubmittedRef.current) return false;
  if (mode === "required") demoRequiredGateOpenRef.current = true;
  setShowNextSteps(false);
  setDemoPromptTime(playbackTime);
  setDemoPrompt(mode);
  return true;
};

const handleDemoPause = (media) => {
  if (!demoGateEnabled || demoSubmittedRef.current) return false;
  const playbackTime = media?.currentTime || 0;
  if (demoRequiredGateOpenRef.current || playbackTime >= requiredPromptAt) {
    return openDemoPrompt("required", Math.min(playbackTime, requiredPromptAt));
  }
  if (playbackTime >= optionalPromptAfter && !demoSoftPromptShownRef.current) {
    demoSoftPromptShownRef.current = true;
    return openDemoPrompt("optional", playbackTime);
  }
  return false;
};

const handleDemoTimeUpdate = (media) => {
  if (!demoGateEnabled || demoSubmittedRef.current || !media || media.currentTime < requiredPromptAt) return false;
  demoRequiredGateOpenRef.current = true;
  media.currentTime = requiredPromptAt;
  media.pause();
  openDemoPrompt("required", requiredPromptAt);
  return true;
};

const handleDemoSeeked = (media) => {
  isSeekingRef.current = false;
  if (!demoGateEnabled || demoSubmittedRef.current || !media || media.currentTime <= requiredPromptAt) return;
  media.currentTime = requiredPromptAt;
  media.pause();
  openDemoPrompt("required", requiredPromptAt);
};

const resumeDemoPlayback = () => {
  demoRequiredGateOpenRef.current = false;
  setDemoPrompt(null);
  playWithDisclaimer().catch(() => {});
};

const completeDemoSubmission = () => {
  demoSubmittedRef.current = true;
  setDemoSubmitted(true);
  writeSessionFlag(demoSubmissionKey);
  resumeDemoPlayback();
};

const logAddressTypingStarted = (value) => {
  if (hasLoggedAddressTypingStartedRef.current || !String(value || "").trim()) return;
  hasLoggedAddressTypingStartedRef.current = true;

  logPodcastAction({
    platform: isMobile ? 'mobile' : 'web',
    action: 'interactive_map_address_typing_started',
    cta: '',
    node: String(nearestNodeIdRef.current || ''),
    param_lang: selectedLanguage,
    chapter: String(String(nearestNodeIdRef.current || '') + "_" + selectedLanguage),
    podcast: selectedSimulation?.name || ''
  });
};

const handleSearchTextInput = (value) => {
  setSearchText(value);
  logAddressTypingStarted(value);
};

const showInstructions = (seg, { alsoOpenLink = false, href } = {}) => {
  setOpenInstructionKey(seg.key);        // open the instructions panel for this item
  playResourceSegment(seg);              // auto-play the audio for this item

  if (alsoOpenLink && href) {
    logPodcastAction({
      platform: isMobile ? 'mobile' : 'web',
      action: 'next_step_clicked_open',
      cta: seg.key,
      node: String(nearestNodeIdRef.current || ''),
      param_lang: selectedLanguage,
      chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
      podcast: selectedSimulation?.name || ''
    });
    try { window.open(href, "_blank", "noopener,noreferrer"); } catch (_) {}
  }
};

const openNextSteps = (source) => {
  if (demoGateEnabled) return;
  logPodcastAction({
    platform: isMobile ? 'mobile' : 'web',
    action: 'next_steps_open',
    cta: source,
    node: String(nearestNodeIdRef.current || ''),
    chapter: String(String(nearestNodeIdRef.current || '') + "_" + selectedLanguage),
    param_lang: selectedLanguage,
    podcast: selectedSimulation?.name || ''
  });
  setShowNextSteps(true);
};

const logSimulationPdfAnalytics = (action) => {
  logPodcastAction({
    platform: isMobile ? 'mobile' : 'web',
    action,
    cta: '',
    node: String(nearestNodeIdRef.current || ''),
    chapter: String(String(nearestNodeIdRef.current || '') + "_" + selectedLanguage),
    param_lang: selectedLanguage,
    podcast: selectedSimulation?.name || ''
  });
};

useEffect(() => {
  const update = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    setDeviceZoom(mobile ? 15 : 16);
  };
  update();
  window.addEventListener("resize", update);
  return () => window.removeEventListener("resize", update);
}, []);


// Share with neighbors (Web Share API, mailto fallback)
const shareWithNeighbors = () => {
  logPodcastAction({
    platform: isMobile?'mobile': 'web',
    action: 'next_step_clicked',
    cta: 'share_with_neighbors',
    node: String(nearestNodeIdRef.current || ''),
    chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
    param_lang: selectedLanguage,
    podcast: selectedSimulation?.name || ''
  });

  const shareData = {
    title: "Wildfire evacuation guide",
    text: "A personalized wildfire evacuation podcast for your street address based on UC Berkeley-based traffic model. Learn about the risks and take next steps to prepare",
    url: window.location.href
  };

  if (navigator.share) {
    navigator.share(shareData).catch(() => {});
  } else {
    const subject = encodeURIComponent(shareData.title);
    const body = encodeURIComponent(`${shareData.text}\n${window.location.href}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }
};

const clearRoutesPodcast = React.useCallback(() => {
  // stop current audio & drop its src
  const a = routesAudioRef.current;
  if (a) { try { a.pause(); } catch {} a.removeAttribute('src'); a.load(); }
  if (routeVideoCameraTimerRef.current) {
    clearTimeout(routeVideoCameraTimerRef.current);
    routeVideoCameraTimerRef.current = null;
  }

  // wipe bundle state so autoplay condition fails
  setRoutesAudioUrl('');
  setRoutesMediaKind('audio');
  setRouteVideoHiddenForCamera(false);
  setRouteScenes([]);
  setRouteTimings({});
  showRouteTimesWhenPausedRef.current = true;
  clearHeroMedia();
  rawScenesRef.current = [];
  resetMediaDiagnostics();
  resetDisclaimer();
}, [clearHeroMedia, resetDisclaimer, resetMediaDiagnostics]);

const attemptRoutesAutoplay = async (source) => {
  const media = routesAudioRef.current;
  if (!wantRoutesAutoplayRef.current || !media || routesAutoplayInFlightRef.current) return false;

  routesAutoplayInFlightRef.current = true;
  setSearchLoadingStep(addressProgressSteps.length - 1);
  logRoutePlaybackDiagnostic("interactive_map_autoplay_attempt", source);
  try {
    media.currentTime = 0;
    await playWithDisclaimer({ restart: true });
    wantRoutesAutoplayRef.current = false;
    autoplayOutcomeRef.current = "succeeded";
    setAutoplayOutcome("succeeded");
    logRoutePlaybackDiagnostic("interactive_map_autoplay_succeeded", source);
    return true;
  } catch (error) {
    wantRoutesAutoplayRef.current = false;
    autoplayOutcomeRef.current = "blocked";
    setAutoplayOutcome("blocked");
    setAddressEntryMode("ready");
    setShowAddressEntryFlow(true);
    const reason = error?.name || error?.message || "play_rejected";
    logRoutePlaybackDiagnostic(
      "interactive_map_autoplay_blocked",
      `${source}:${reason}`
    );
    console.warn("Route podcast autoplay was blocked:", error);
    return false;
  } finally {
    routesAutoplayInFlightRef.current = false;
  }
};

useEffect(() => {
  if (mapReady) {
    logPodcastAction({
      platform: isMobile?'mobile': 'web',
      action: 'interactive_map_load',
      cta: '',
      chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
      param_lang: selectedLanguage,
      podcast: selectedSimulation?.name || ''
    });
  }
}, [mapReady, selectedSimulation]);

const loadRouteTableForNode = async (nodeId) => {
  if (!nodeId) return [];

  try {
    setRouteTableLoading(true);
    setRouteTableError('');

    const db = getFirestore();
    let snap = null;
    if (podcastId) {
      snap = await getDoc(doc(db, "podcasts", String(podcastId), "podcast_details", String(nodeId)));
    }
    if (!snap || !snap.exists()) {
      snap = await getDoc(doc(db, "podcast_details", String(nodeId)));
    }

    if (!snap.exists()) {
      setRouteTableRows([]);
      return [];
    }

    console.log(snap.data())

    const payload = snap.data();
    additionalHouseholdNodesRef.current = Array.isArray(payload?.additionalHouseholdNodes)
      ? payload.additionalHouseholdNodes.map(String).filter(Boolean)
      : [];

    const rawChecklist =
    Array.isArray(payload?.routeChecklistItems) ? payload.routeChecklistItems :
    Array.isArray(payload?.checklistItems) ? payload.checklistItems :
    [];

    // Normalize: ensure nodeId exists (in your screenshot it looks like id/key are "0")
    setChecklistItems(
      rawChecklist.map(it => ({
        ...it,
        nodeId: it?.nodeId ?? it?.id ?? it?.key,     // pick one
        title: String(it?.title || "")
      }))
    );


   // Support { routeTables: [...] } (your doc), { routes: [...] }, or a top-level array
   const rows = Array.isArray(payload?.routeTables)? payload.routeTables: Array.isArray(payload?.routes)
   ? payload.routes: Array.isArray(payload)
   ? payload: [];
    const normalizedRows = Array.isArray(rows) ? rows : [];
    setRouteTableRows(normalizedRows);

    // build linkId -> queuedVehicles (lateEvac) index from route tables
    const idx = new Map();
    (rows || []).forEach(r => {
      const arr = Array.isArray(r.queuedVehiclesLate) ? r.queuedVehiclesLate : [];
      arr.forEach(item => {
        const linkId = item && item.linkId != null ? String(item.linkId) : null;
        if (!linkId) return;
        const count = Number(item.queuedVehicles || 0);
        idx.set(linkId, count);
      });
    });
    lateQueuesRef.current = idx;

    // NEW: indexes for ON-TIME views
   const byDest = new Map();   // destId -> Map(linkId -> queuedVehicles)
   const totals = new Map();   // destId -> total seconds (leaveOnTime)

   (rows || []).forEach(r => {
     const destId = String(r.id ?? '');
     if (!destId) return;

     // Per-link queued vehicles for ON TIME
     const qmap = new Map();
     const arr = Array.isArray(r.queuedVehiclesOnTime) ? r.queuedVehiclesOnTime : [];
     arr.forEach(item => {
       const linkId = item && item.linkId != null ? String(item.linkId) : null;
       if (!linkId) return;
       qmap.set(linkId, Number(item.queuedVehicles || 0));
     });
     byDest.set(destId, qmap);

     // Parse "leaveOnTime" like "17 min" -> seconds
     const secs = parseDurationToSeconds(r.leaveOnTime);
      totals.set(destId, secs)
   });
   onTimeQueuesByDestRef.current = byDest;
   onTimeTotalsByDestRef.current = totals;

   return normalizedRows;

  } catch (e) {
    console.error("Failed to load route table:", e);
    setRouteTableError('Could not load evacuation times for this location.');
    return [];
  } finally {
    setRouteTableLoading(false);
  }
};

async function loadRouteBundleFromFirestore(nodeId, lang) {

    if (!podcastId) throw new Error("Missing podcastId");

  const db = getFirestore();
  const storage = getStorage();
  const langCode = String(lang || "EN").toUpperCase();
  const nid = String(nodeId);

  console.log("podcast_details/",nid,langCode)

  // 1) fetch all three docs in parallel
  const [metaSnap, scenesSnap, timingsSnap] = await Promise.all([
    getDoc(doc(db, "podcasts", String(podcastId), "podcast_details", nid, langCode, "podcastMetadata")),
    getDoc(doc(db, "podcasts", String(podcastId), "podcast_details", nid, langCode, "ROUTE_SCENES")),
    getDoc(doc(db, "podcasts", String(podcastId), "podcast_details", nid, langCode, "routeAudioTimings"))
  ]);


  if (!metaSnap.exists()) throw new Error("podcastMetadata missing");
  if (!scenesSnap.exists()) throw new Error("ROUTE_SCENES missing");
  if (!timingsSnap.exists()) {
    console.warn("routeAudioTimings missing; continuing with empty timings.");
  }

  // 2) resolve media URL from Storage
  const meta = metaSnap.data() || {};
  showRouteTimesWhenPausedRef.current = meta.showRouteTimesWhenPaused !== false;
  const objectPath = meta.objectPath || "";      // e.g. "PodcastMedia/.../Lakeview-Drive_1760901871.mp3"
  const gsUri = meta.gsUri || "";                // e.g. "gs://<bucket>/<path>"
  let mediaUrl = "";
  const mediaKind = inferRouteMediaKind(meta);
  const canonicalExtension = mediaKind === "video" ? "mp4" : "mp3";
  const canonicalPath = `Podcasts/${String(podcastId)}/${langCode}/${nid}_${langCode}.${canonicalExtension}`;

  // The editor publishes browser-ready media to this canonical location. Prefer
  // it over older metadata paths, which may reference a WAV/container that is
  // not supported consistently across browsers.
  mediaUrl = await getDownloadURL(storageRef(storage, canonicalPath)).catch(() => "");

  if (!mediaUrl && objectPath) {
    mediaUrl = await getDownloadURL(storageRef(storage, objectPath));
  } else if (!mediaUrl && gsUri && gsUri.startsWith("gs://")) {
    const [, bucketAndKey] = gsUri.split("gs://");
    const firstSlash = bucketAndKey.indexOf("/");
    const key = firstSlash >= 0 ? bucketAndKey.slice(firstSlash + 1) : "";
    mediaUrl = await getDownloadURL(storageRef(storage, key));
  } else if (!mediaUrl) {
    throw new Error("No valid objectPath or gsUri on podcastMetadata");
  }

  // 3) extract scene/timing content
  console.log("Scenes doc", scenesSnap.data())
  console.log("Timings doc", timingsSnap.exists() ? timingsSnap.data() : null)

  const rawScenes = Array.isArray(scenesSnap.data()?.content)
  ? scenesSnap.data().content
  : [];
   
  const timings = timingsSnap.exists() ? (timingsSnap.data()?.content || {}) : {};

  // Decide which default location symbol to use based on presence of key "28"
  const hasEstudilloAlt = Object.keys(timings).some((k) => String(k).includes("I-580 North"));
  const heuristicDefault = hasEstudilloAlt
    ? SCENE_SYMBOLS.DEFAULT_LOCATION_2
    : SCENE_SYMBOLS.DEFAULT_LOCATION_1;

  // IMPORTANT: build a *local* symbols map (do NOT mutate SCENE_SYMBOLS)
  const symbols = {
    ...SCENE_SYMBOLS,
    DEFAULT_LOCATION: normalizeLatLng(defaultLocation) || heuristicDefault,
    DEFAULT_ZOOM: Number.isFinite(Number(defaultZoom)) ? Math.round(Number(defaultZoom)) : SCENE_SYMBOLS.DEFAULT_ZOOM
  };

  const scenes = normalizeScenes(rawScenes, { symbols });
  rawScenesRef.current = scenes;
  const resolvedScenes = attachSavedPolygons(scenes);

  // 4) push into state (so memos depending on these re-compute)
  setRouteScenes(resolvedScenes);
  setRouteTimings(timings);
  setRoutesAudioUrl(mediaUrl);
  setRoutesMediaKind(mediaKind);

  // let the caller decide when/how to play; this guarantees the bundle is loaded
  return { scenes, timings, audioUrl: mediaUrl, mediaKind };
}

useEffect(() => {
  return () => {
    if (routeVideoCameraTimerRef.current) {
      clearTimeout(routeVideoCameraTimerRef.current);
      routeVideoCameraTimerRef.current = null;
    }
  };
}, []);

useEffect(() => {
  const update = () => {
    const z = window.innerWidth <= 768 ? 15 : 16;
    setDeviceZoom(z);
  };
  update();
  window.addEventListener("resize", update);
  return () => window.removeEventListener("resize", update);
}, []);

useEffect(() => {
  if (!rawScenesRef.current || !rawScenesRef.current.length) return;
  setRouteScenes(attachSavedPolygons(rawScenesRef.current));
}, [attachSavedPolygons]);

useEffect(() => {
  const map = mapRef.current;
  if (!map || typeof window === "undefined" || !window.google?.maps?.MapTypeId) return;
  const targetType = isMapShaking ? window.google.maps.MapTypeId.HYBRID : window.google.maps.MapTypeId.ROADMAP;
  map.setMapTypeId(targetType);
  setCurrentMapTypeId(targetType);
}, [isMapShaking]);

const storyboardDefaultZoom = React.useMemo(() => {
  const z = Number(defaultZoom);
  return Number.isFinite(z) ? Math.round(z) : defaultZoom;
}, [defaultZoom]);

const resolveStoryboardZoom = React.useCallback(
  (zoom) => {
    if (zoom == null || zoom === "DEFAULT_ZOOM") return storyboardDefaultZoom;
    if (zoom === "DEEPER_ZOOM") return storyboardDefaultZoom + 1;
    return zoom;
  },
  [storyboardDefaultZoom]
);

const scenesEN = React.useMemo(
  () =>
    (routeScenes || []).map(s => ({
      ...s,
      camera: s.camera ? { ...s.camera, zoom: resolveStoryboardZoom(s.camera.zoom) } : s.camera
    })),
  [routeScenes, resolveStoryboardZoom]
);

const scenesES = React.useMemo(
  () =>
    (routeScenes || []).map(s => ({
      ...s,
      camera: s.camera ? { ...s.camera, zoom: resolveStoryboardZoom(s.camera.zoom) } : s.camera
    })),
  [routeScenes, resolveStoryboardZoom]
);

const openResourceAndPlay = (seg, href) => {
  showInstructions(seg, { alsoOpenLink: true, href });
};

// One animation loop per polyline
const linkAnimRef = useRef(new Map()); // polyline -> { raf, cars:[], color:"#186EE3", fft:5 }

const GET_TRAFFIC_DOT_PROFILE = () => {
  return getTrafficDotProfile(mapRef.current?.getZoom?.());
};

const GET_HOUSE_MARKER_ICON = () => {
  const size = getHouseMarkerSize(mapRef.current?.getZoom?.());

  return {
    url: LocationIcon,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2)
  };
};

const UPDATE_HOUSE_MARKER_SIZES = () => {
  if (!window.google?.maps) return;
  const icon = GET_HOUSE_MARKER_ICON();
  circleRef.current?.setIcon?.(icon);
  additionalHouseMarkersRef.current.forEach(marker => marker?.setIcon?.(icon));
};

const STOP_ANIMS = () => {
  linkAnimRef.current.forEach(({ raf, poly }) => {
    if (raf) cancelAnimationFrame(raf);
    // also clear icons so no dots are left
    if (poly) poly.set('icons', []);
  });
  linkAnimRef.current.clear();
};

// show/hide the animated dots on a given polyline
const SET_LINK_ANIM_VISIBLE = (poly, visible) => {
  const st = linkAnimRef.current.get(poly);
  if (!st) return;
  st.visible = !!visible;
  if (!visible) {
    // immediately clear any existing dots
    try { st.poly.set('icons', []); } catch (_) {}
  }
};

// start or restart animation for a single polyline
const START_LINK_ANIM = (poly, color, fftSec, linkId) => {
  const prev = linkAnimRef.current.get(poly);
  if (prev) {
    if (prev.raf) cancelAnimationFrame(prev.raf);
  }

  const state = {
    poly,
    color,                 // moving-dot color, and also used for stroke
    baseColor: color,      // remember original so we can restore after a scene
    baseQueueColor: color,
    linkId: linkId != null ? String(linkId) : null,
    fft: Math.max(0.5, Number(fftSec) || 5),
    cars: [],              // { born:number(ms) }
    lastSpawnAt: performance.now(),
    raf: 0,
    running: true,
    visible: true,
    queue: {
      count: 0,
      baseCount: 0,
      currentCount: 0,     // what we render *right now*
      targetCount: 0,      // what we're animating *toward*
      countTween: { active: false, from: 0, to: 0, start: 0, dur: 800 },
      spacingPct: 4,
      color: '#186EE3',
      multiplier: 1,
      tween: { active: false, from: 1, to: 1, start: 0, dur: 800 } // animate multiplier
    }
  };

  const tick = () => {

    const easeInOut = (u) => (u < 0.5 ? 2 * u * u : -1 + (4 - 2 * u) * u);

    if (!state.running) return;
    const now = performance.now();
    const icons = [];
    const durMs = state.fft * 1000;
    const dotProfile = GET_TRAFFIC_DOT_PROFILE();

    if (now - state.lastSpawnAt >= dotProfile.spawnIntervalMs) {
      state.cars.push({ born: now });
      state.lastSpawnAt = now;
      if (state.cars.length > 120) state.cars.splice(0, state.cars.length - 120);
    }

    const gm = window?.google?.maps;
    if (!gm) {
      state.raf = requestAnimationFrame(tick);
      return;
    }

    if (!state.visible) {
      state.poly.set('icons', []);
      state.raf = requestAnimationFrame(tick);
      return;
    }

    // Effective queued count after scene multiplier
    const q = state.queue;

    // tween multiplier (ease in/out)
    let effMult = Math.max(0, q.multiplier);
    if (q.tween && q.tween.active) {
      const elapsed = now - q.tween.start;
      const u = Math.min(1, elapsed / Math.max(1, q.tween.dur));
      const ease = easeInOut(u);
      effMult = q.tween.from + (q.tween.to - q.tween.from) * ease;
      if (u >= 1) {
        q.tween.active = false;
        q.multiplier = q.tween.to;
      }
    }

    let currCount = q.currentCount;
    if (q.countTween && q.countTween.active) {
      const elapsed = now - q.countTween.start;
      const u = Math.min(1, elapsed / Math.max(1, q.countTween.dur));
      const ease = easeInOut(u);
      currCount = q.countTween.from + (q.countTween.to - q.countTween.from) * ease;
      if (u >= 1) {
        q.countTween.active = false;
        currCount = q.countTween.to;
      }
      q.currentCount = currCount; // persist the in-flight value
    }

    // translate tweened multiplier into queued span (dots)
    const effCount = Math.max(0, Math.floor(currCount * effMult));
    const queuedSpanPct = effCount * Math.max(1, q.spacingPct);
    const freeEndPct = Math.max(0, 100 - queuedSpanPct);

    // moving dots only in the free portion
    for (let i = state.cars.length - 1; i >= 0; i--) {
      const age = now - state.cars[i].born;
      if (age >= durMs) { state.cars.splice(i, 1); continue; }
      const travelPct = Math.min(100, (age / durMs) * 100);
      if (travelPct <= freeEndPct) {
        icons.push({
          icon: {
            path: gm.SymbolPath.CIRCLE,
            scale: dotProfile.scale,
            strokeOpacity: 0,
            fillOpacity: 1,
            fillColor: state.color
          },
          offset: `${travelPct}%`
        });
      }
    }

    let effectiveCount = effCount;

    // queued dots from the end backward (clipped to link)
    if (effectiveCount > 0) {
      const qIcon = {
        path: gm.SymbolPath.CIRCLE,
        scale: dotProfile.scale,
        strokeOpacity: 0,
        fillOpacity: 1,
        fillColor: q.color || '#FF3B30'
      };
      for (let i = 0; i < effectiveCount; i++) {
        const offsetFromEnd = i * Math.max(1, q.spacingPct);
        const offset = 100 - offsetFromEnd;
        if (offset < freeEndPct) break;
        icons.push({ icon: qIcon, offset: `${Math.max(freeEndPct, Math.min(100, offset))}%` });
      }
    }

    state.poly.setOptions({ strokeOpacity: 0.35, strokeColor: state.color });
    state.poly.set('icons', icons);
    state.raf = requestAnimationFrame(tick);
  };

  state.raf = requestAnimationFrame(tick);
  linkAnimRef.current.set(poly, state);
};

// update color (e.g., when you flip a link to orange)
const UPDATE_LINK_ANIM_COLOR = (poly, newColor) => {
  const st = linkAnimRef.current.get(poly);
  if (st) st.color = newColor;
};

const SET_LINK_QUEUE = (poly, count, spacingPct = 4, color, opts = {}) => {
  const st = linkAnimRef.current.get(poly);
  if (!st) return;
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const q = st.queue;
  const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  const { animate = true, duration = 1200, persistBaseline = true } = opts;

  // Store/keep the ON-TIME baseline (first time only, unless explicitly told not to)
  if (persistBaseline) {
    if (q.onTimeCount == null) q.onTimeCount = n;
    if (q.baseCount == null) q.baseCount = n;
    if (color && !st.baseQueueColor) st.baseQueueColor = color;
  }

  // if no prior currentCount, initialize
  if (typeof q.currentCount !== 'number') q.currentCount = 0;

  // set up tween to the new target
  q.targetCount = n;
  if (animate) {
    q.countTween = {
      active: true,
      from: q.currentCount,
      to: n,
      start: now,
      dur: duration
    };
  } else {
    q.countTween = { active: false, from: n, to: n, start: now, dur: 0 };
    q.currentCount = n;
  }
  q.spacingPct = Math.max(1, Number(spacingPct) || 4);
  if (color) {
    q.color = color;
    if (persistBaseline) st.baseQueueColor = st.baseQueueColor || color;
  }
  
};

const APPLY_LATE_QUEUE_COUNTS = () => {
  linkAnimRef.current.forEach(st => {
    const id = st.linkId ? String(st.linkId) : null;
    if (!id) return;
    const q = lateQueuesRef.current.get(id);
    if (q != null) {
      SET_LINK_QUEUE(st.poly, q, 4, "#FF0000", { animate: true, duration: 1400, persistBaseline: false }); // red queue dots while scene is “red”
    }
  });
};

const CLEAR_QUEUE_OVERRIDE = () => {
  linkAnimRef.current.forEach(st => {
    const base = (st.queue.onTimeCount != null ? st.queue.onTimeCount: (st.queue.baseCount != null ? st.queue.baseCount : 0));
    SET_LINK_QUEUE(st.poly, base, st.queue.spacingPct, st.baseQueueColor, { animate: true, duration: 600, persistBaseline: true });
  });
};

const APPLY_TRAFFIC_OVERRIDE = (spec) => {
  const globalColor = spec?.color;
  const targetMultRaw = (typeof spec?.congestionMultiplier === 'number' && !Number.isNaN(spec.congestionMultiplier))
    ? Math.max(0, spec.congestionMultiplier)
    : null;

  const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

  linkAnimRef.current.forEach((st) => {
    if (globalColor) {
      st.color = globalColor;
      st.queue.color = globalColor;
    }

    if (targetMultRaw === null) return;

    const q = st.queue;

    // compute the *current* effective multiplier (don’t snap back to from/to)
    let curr = q.multiplier;
    if (q.tween && q.tween.active) {
      const elapsed = now - q.tween.start;
      const u = Math.min(1, elapsed / Math.max(1, q.tween.dur));
      curr = q.tween.from + (q.tween.to - q.tween.from) * u;
    }

    // if target hasn’t changed, don’t restart the tween
    if (Math.abs(targetMultRaw - curr) < 1e-3) return;

    const to = targetMultRaw;
    const dur = to > curr ? 3000 : 1000; // 3s to build congestion, faster to clear

    q.tween = {
      active: true,
      from: curr,
      to,
      start: now,
      dur
    };
  });
};

const CLEAR_TRAFFIC_OVERRIDE = () => {
  const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();

  linkAnimRef.current.forEach((st) => {
    const from = (st.queue.tween && st.queue.tween.active) ? st.queue.tween.to : st.queue.multiplier;

    // decongest quickly back to baseline
    st.queue.tween = {
      active: true,
      from,
      to: 1,
      start: now,
      dur: 500
    };

    st.color = st.baseColor || st.color;
    st.queue.color = st.baseQueueColor || '#186EE3';
  });
};

const activeScenesRef = useRef([]);
useEffect(() => {
  activeScenesRef.current = isEnglish ? scenesEN : scenesES;
}, [isEnglish, scenesEN, scenesES]);

useEffect(() => {
  if (prefillDoneRef.current) return;

  const qs = typeof window !== "undefined" ? window.location.search : "";
  if (!qs) return;

  const params = new URLSearchParams(qs.replace(/\?\s*/, "?").replace(/&&/g, "&"));
  const streetParam = params.get("street");

  if (streetParam) {
    const streetText = decodeURIComponent(streetParam).replace(/\+/g, " ").trim();
    setSearchText(streetText);
    setShowAddressEntryFlow(true);
    setAddressEntryMode("form");
    setAddressEntryError("");
    setResolvedAddressLabel("");
    prefillDoneRef.current = true;
  }
  // URL prefill only seeds the search box. The user must submit to build/play the route.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


const nextStepsRuntimeConfig = React.useMemo(
  () => resolveNextStepsRuntimeConfig({
    config: nextStepsFaqConfig,
    selectedLanguage
  }),
  [selectedLanguage, nextStepsFaqConfig]
);
const { resourceSegments, resourceMeta, resourceUI, faqList } = nextStepsRuntimeConfig;
const nextStepAudio = useNextStepAudio({
  podcastId,
  selectedLanguage,
  nextStepsConfig: nextStepsFaqConfig,
  isConfigLoading: nextStepsFaqLoading,
  resourceSegments
});
const {
  audioRef: resourcesAudioRef,
  audioUrl: resourcesAudioUrl,
  activeResourceKey,
  resourceProgress,
  resourceIsPlaying,
  setResourceIsPlaying,
  canPlayStep: isPlayableSegment,
  segmentDuration,
  playStep: playResourceSegment,
  seekStep: seekWithinResourceSegment,
  stop: stopResourcePlayback
} = nextStepAudio;

const guidedNextSteps = React.useMemo(() => {
  const resourceItems = (resourceSegments || []).map((seg) => ({
    type: "resource",
    key: seg.key,
    seg,
    meta: resourceMeta[seg.key] || {}
  }));

  return [
    ...resourceItems,
    {
      type: "share",
      key: "share_with_neighbors",
      meta: {
        title: resourceUI.share,
        subtitle: resourceUI.share_desc
      }
    }
  ];
}, [resourceSegments, resourceMeta, resourceUI.share, resourceUI.share_desc]);

const activeNextStep = guidedNextSteps[
  Math.min(activeNextStepIndex, Math.max(guidedNextSteps.length - 1, 0))
];
const completedNextStepSet = React.useMemo(
  () => new Set(completedNextStepKeys),
  [completedNextStepKeys]
);

useEffect(() => {
  if (!guidedNextSteps.length) {
    setActiveNextStepIndex(0);
    return;
  }

  setActiveNextStepIndex((prev) =>
    Math.min(prev, Math.max(guidedNextSteps.length - 1, 0))
  );
}, [guidedNextSteps.length]);

const markNextStepComplete = (key) => {
  setCompletedNextStepKeys((prev) =>
    prev.includes(key) ? prev : [...prev, key]
  );
};

const toggleGuidedStepDetails = (key, defaultHidden = false) => {
  const stateKey = defaultHidden ? `show:${key}` : key;
  setHiddenNextStepKeys((prev) =>
    prev.includes(stateKey)
      ? prev.filter((value) => value !== stateKey)
      : [...prev, stateKey]
  );
};

const goToNextGuidedStep = () => {
  setActiveNextStepIndex((prev) =>
    Math.min(prev + 1, Math.max(guidedNextSteps.length - 1, 0))
  );
  setOpenInstructionKey(null);
  stopResourcePlayback();
};

const skipActiveGuidedStep = () => {
  if (!activeNextStep) return;

  logPodcastAction({
    platform: isMobile ? 'mobile' : 'web',
    action: 'next_step_skipped',
    cta: activeNextStep.key,
    node: String(nearestNodeIdRef.current || ''),
    chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
    param_lang: selectedLanguage,
    podcast: selectedSimulation?.name || ''
  });
  goToNextGuidedStep();
};

const setSafetyCalloutsVisible = (show) => {
  if (!safetyCalloutsRef.current) return;
  const shouldShow = show && showRouteTimesWhenPausedRef.current;
  safetyCalloutsRef.current.forEach(ov => {
    if (!ov) return;
    ov.setMap(shouldShow ? mapRef.current : null);
  });
};

// Create (or update) a polyline for a link id with a given color
const ensureLinkPolyline = (linkId, color = "#186EE3") => {
  const id = String(linkId);
  const map = mapRef.current;
  if (!map) return null;

  let poly = storyboardLinkPolysRef.current.get(id);
  if (!poly) {
    const wkt = linkGeomsRef.current[id];
    if (!wkt) return null;
    const coords = wkt
      .replace(/^LINESTRING\s*\(/i, "")
      .replace(/\)$/, "")
      .split(",")
      .map(pt => {
        const [lng, lat] = pt.trim().split(" ").map(Number);
        return { lat, lng };
      });

    poly = new window.google.maps.Polyline({
      path: coords,
      geodesic: true,
      strokeColor: color,
      strokeOpacity: 1,
      strokeWeight: 5,
      zIndex: 1000,
      clickable: false
    });
    storyboardLinkPolysRef.current.set(id, poly);
  }

  poly.setOptions({ strokeColor: color, strokeOpacity: 1 });
  if (!poly.getMap()) poly.setMap(map);
  return poly;
};

// Create (or update) a marker for a node id
const ensureNodeMarker = (nodeId, { iconUrl, color } = {}) => {
  const id = String(nodeId);
  const map = mapRef.current;
  if (!map || !graphData?.nodes?.[id]) return null;

  let marker = storyboardNodeMarkersRef.current.get(id);
  const { lat, lng } = graphData.nodes[id];

  const makeIcon = () => {
    if (iconUrl) {
      return {
        url: iconUrl,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
      };
    }
    // simple colored dot if no icon
    const pinSvg = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="${color || "#186EE3"}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" />
      </svg>`;
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(pinSvg),
      scaledSize: new window.google.maps.Size(24, 24),
      anchor: new window.google.maps.Point(12, 12),
    };
  };

  if (!marker) {
    marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      icon: makeIcon(),
      opacity: 1,
    });
    storyboardNodeMarkersRef.current.set(id, marker);
  } else {
    marker.setPosition({ lat, lng });
    marker.setIcon(makeIcon());
    if (!marker.getMap()) marker.setMap(map);
  }
  return marker;
};

const ensureCustomMarker = ({ key, lat, lng, iconUrl, color }) => {
  const map = mapRef.current;
  if (!map || typeof lat !== "number" || typeof lng !== "number") return null;

  const id = String(key || `latlng:${lat},${lng}`);
  let marker = storyboardNodeMarkersRef.current.get(id);

  const makeIcon = () => {
    if (iconUrl) {
      return {
        url: iconUrl,
        scaledSize: new window.google.maps.Size(60, 60),
        anchor: new window.google.maps.Point(20, 20),
      };
    }
    const pinSvg = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="${color || "#186EE3"}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="8" />
      </svg>`;
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(pinSvg),
      scaledSize: new window.google.maps.Size(24, 24),
      anchor: new window.google.maps.Point(12, 12),
    };
  };

  if (!marker) {
    marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      icon: makeIcon(),
      opacity: 1,
    });
    storyboardNodeMarkersRef.current.set(id, marker);
  } else {
    marker.setPosition({ lat, lng });
    marker.setIcon(makeIcon());
    if (!marker.getMap()) marker.setMap(map);
  }
  return marker;
};

// Hide everything created by the storyboard
const hideAllStoryboardOverlays = () => {
  storyboardLinkPolysRef.current.forEach(poly => poly.setMap(null));
  storyboardNodeMarkersRef.current.forEach(m => m.setMap(null));
  storyboardFirePolysRef.current.forEach(p => p.setMap(null)); 
  storyboardFirePolysRef.current = [];
  storyboardSmokeOverlaysRef.current.forEach(overlay => overlay?.setMap(null));
  storyboardSmokeOverlaysRef.current.clear();
  sceneTimeOverlaysRef.current.forEach(ov => ov && ov.setMap(null));
  sceneTimeOverlaysRef.current = [];                     
};

const showSceneDestTimes = (scene) => {
  // clear previous
  sceneTimeOverlaysRef.current.forEach(ov => ov && ov.setMap(null));
  sceneTimeOverlaysRef.current = [];

  const list = Array.isArray(scene?.destTimes) ? scene.destTimes : [];
  if (!list.length) return;

  list.forEach(dt => {
    const id = String(dt.nodeId);

    // 1) Prefer the drawn destination marker position (if this id was one of the selected routes)
    let pos = null;
    const r = (routesDataRef.current || []).find(rd => String(rd.destNodeId) === id);
    if (r?.destinationPos) pos = r.destinationPos;

    // 2) Fallback: place it at the graph node coordinates (works for exits like node "0")
    if (!pos && graphData?.nodes?.[id]) {
      pos = { lat: graphData.nodes[id].lat, lng: graphData.nodes[id].lng };
    }

    if (!pos) return;

    const ov = createTextOverlay(pos, String(dt.time));
    sceneTimeOverlaysRef.current.push(ov);
  });
};

const getUserCenter = () => {
  const markerPos = circleRef.current?.getPosition?.();
  if (markerPos?.toJSON) return markerPos.toJSON();
  return normalizeLatLng(selectedLocation);
};

const resolveStoryboardCenter = (center) => {
  if (center === "USER_CENTER") return getUserCenter();
  return center;
};

// Apply a storyboard "scene" object { camera, links[], nodes[], ... }
const applyStoryboardScene = (scene) => {
  if (!scene) return;

  // Camera (smooth if changed)
  if (scene.camera && mapRef.current) {
    const { zoom } = scene.camera;
    const center = resolveStoryboardCenter(scene.camera.center);
    if (!center) return;
    const last = lastStoryboardCameraRef.current;
    const changed =
      !last ||
      Math.abs((last.center?.lat ?? 0) - center.lat) > 1e-6 ||
      Math.abs((last.center?.lng ?? 0) - center.lng) > 1e-6 ||
      (last.zoom !== zoom);

    if (changed) {
      const shouldHideVideoForCamera =
        routesMediaKind === "video" &&
        routesAudioRef.current &&
        !routesAudioRef.current.paused;
      if (shouldHideVideoForCamera) {
        if (routeVideoCameraTimerRef.current) {
          clearTimeout(routeVideoCameraTimerRef.current);
        }
        setRouteVideoHiddenForCamera(true);
        routeVideoCameraTimerRef.current = setTimeout(() => {
          setRouteVideoHiddenForCamera(false);
          routeVideoCameraTimerRef.current = null;
        }, 1000);
      }
      modifyMapCamera(mapRef.current, center, zoom, 1000);
      lastStoryboardCameraRef.current = { center, zoom };
    }
  }

  // LINKS: force red + high z-index unless overridden in scene
  const visibleLinks = new Set();
  (scene.links || []).forEach(l => {
    const poly = ensureLinkPolyline(l.id, l.color || "#FF0000");
    if (poly) {
      poly.setOptions({
        strokeColor: l.color || "#FF0000",
        zIndex: typeof l.zIndex === "number" ? l.zIndex : 9999,
        strokeOpacity: 1
      });
      visibleLinks.add(String(l.id));
    }
  });
  storyboardLinkPolysRef.current.forEach((poly, id) => {
    if (!visibleLinks.has(id)) poly.setMap(null);
  });

  // Markers (nodes + custom icons share one registry)
  const visibleMarkerKeys = new Set();

  // Legacy node markers still supported
  (scene.nodes || []).forEach(n => {
    const marker = ensureNodeMarker(n.nodeId, { iconUrl: n.iconUrl, color: n.color });
    if (marker) visibleMarkerKeys.add(String(n.nodeId));
  });

  // NEW: icons by lat/lng; currently supports { type: "flag", lat, lng, key? }
  (scene.icons || []).forEach(icon => {
      if (typeof icon.lat !== "number" || typeof icon.lng !== "number") return;
      const type = String(icon.type || "flag").toLowerCase();
      const iconUrl =
        type === "parking" ? ParkingIconGreen : /* school uses flag too */ SafetyIcon;
      const key = String(icon.key || `${type}:${icon.lat},${icon.lng}`);
      const marker = ensureCustomMarker({
        key,
        lat: icon.lat,
          lng: icon.lng,
          iconUrl
        });
        if (marker) visibleMarkerKeys.add(key);
      });

  // Hide any storyboard markers not declared in this scene
  storyboardNodeMarkersRef.current.forEach((m, id) => {
    if (!visibleMarkerKeys.has(id)) m.setMap(null);
  });

  storyboardFirePolysRef.current.forEach(p => p.setMap(null));
  storyboardFirePolysRef.current = [];

  if (scene.firePolygons && scene.firePolygons.length && mapRef.current) {
    const baseColor = "rgb(255,69,0)";
    const visibleSmokeKeys = new Set();

    scene.firePolygons.forEach(fp => {
      if (!Array.isArray(fp?.path) || fp.path.length < 3) return;

      const polygonTypes = String(fp.type || "")
        .toLowerCase()
        .split("|")
        .map((type) => type.trim())
        .filter(Boolean);
      const hasSmoke = polygonTypes.includes("smoke");
      const hasFire = polygonTypes.length === 0 || polygonTypes.includes("fire");

      if (hasSmoke) {
        const smokeKey = getSmokeOverlayKey(fp);
        visibleSmokeKeys.add(smokeKey);

        if (!storyboardSmokeOverlaysRef.current.has(smokeKey)) {
          const overlay = createSmokeEmberOverlay({
            google: window.google,
            map: mapRef.current,
            path: fp.path,
            wind: fp.wind,
            density: fp.density
          });
          if (overlay) storyboardSmokeOverlaysRef.current.set(smokeKey, overlay);
        }
        if (!hasFire) return;
      }

      const polygon = new window.google.maps.Polygon({
        paths: fp.path,
        fillColor: fp.color || baseColor,
        fillOpacity: typeof fp.fillOpacity === "number" ? fp.fillOpacity : 0.8,
        strokeColor: fp.color || baseColor,
        strokeOpacity: typeof fp.strokeOpacity === "number" ? fp.strokeOpacity : 0.8,
        strokeWeight: typeof fp.strokeWeight === "number" ? fp.strokeWeight : 2,
        map: mapRef.current,
        zIndex: typeof fp.zIndex === "number" ? fp.zIndex : 9998
      });
      storyboardFirePolysRef.current.push(polygon);
    });

    storyboardSmokeOverlaysRef.current.forEach((overlay, smokeKey) => {
      if (!visibleSmokeKeys.has(smokeKey)) {
        overlay?.setMap(null);
        storyboardSmokeOverlaysRef.current.delete(smokeKey);
      }
    });
  } else {
    storyboardSmokeOverlaysRef.current.forEach(overlay => overlay?.setMap(null));
    storyboardSmokeOverlaysRef.current.clear();
  }

  showSceneDestTimes(scene);
  setIsMapShaking(Boolean(scene.earthquake));
};

// Given a current time (seconds), find the scene and apply it
const applyStoryboardForTime = (tSec) => {
  const scenes = activeScenesRef.current || [];
  if (!scenes.length) {
    hideAllStoryboardOverlays();
    lastStoryboardCameraRef.current = null;
    setActiveCallout(null);
    setBalloonsSpec(null);
    clearHeroMedia();
    CLEAR_TRAFFIC_OVERRIDE();
    setIsMapShaking(false);
    setSceneFlooding(false);
    return;
  }
  const scene = scenes.find(s => tSec >= s.start && tSec < s.end);
  if (!scene) {
    hideAllStoryboardOverlays();
    lastStoryboardCameraRef.current = null;
    setActiveCallout(null);
    setBalloonsSpec(null);
    clearHeroMedia();
    CLEAR_TRAFFIC_OVERRIDE();
    setIsMapShaking(false);
    setSceneFlooding(false);
    return;
  }
  applyStoryboardScene(scene);
  setActiveCallout(scene.callout || null);
  setBalloonsSpec(scene.balloons || null);
  applyHeroMediaForScene(scene, tSec);

  if (scene.trafficAnimation) {
    // If the scene’s congestion color is “red”, drive queues from route tables.
    const col = String(scene.trafficAnimation.color || "").toLowerCase();
    if (col.includes("ff0000") || col.includes("red")) {
      APPLY_LATE_QUEUE_COUNTS();
    } else {
      CLEAR_QUEUE_OVERRIDE();
    }
    // Apply the scene color last so restoring baseline queue counts cannot
    // overwrite it with the baseline queue color.
    APPLY_TRAFFIC_OVERRIDE(scene.trafficAnimation);
  } else {
    CLEAR_TRAFFIC_OVERRIDE();
    CLEAR_QUEUE_OVERRIDE();
  }
  setSceneFlooding(Boolean(scene.flooding));
};


  // Fetch podcast audio URL on mount.
  useEffect(() => {
    const storage = getStorage();
    const podcastRefStorage = storageRef(storage, 'Podcast media/street-podcasts/Lakeview-drive/Lakeview-Drive_1760738279.mp3');
    getDownloadURL(podcastRefStorage)
      .then((url) => { setPodcastAudioUrl(url); })
      .catch((error) => { console.error("Error fetching podcast audio:", error); });

  }, []);

  useEffect(() => {
    if (!selectedSimulation) return;
    let cancelled = false;

    // Clear out any previous graph data immediately
    setGraphData({ links: [], nodes: {}, adjList: {} });
    setSimulationDataLoading((current) => ({ ...current, links: true }));

    const asset = "links_csv";
    fetchStorageText(
      `${selectedSimulation.id}/inputs/berkeley_links.csv`,
      (error, attempt, maxAttempts) => {
        const detail = `${asset}:attempt_${attempt}:${storageErrorName(error)}`;
        console.warn(`Simulation ${asset} load attempt ${attempt}/${maxAttempts} failed.`, error);
        logRoutePlaybackDiagnostic("interactive_map_storage_load_error", detail);
        if (attempt === maxAttempts) {
          logRoutePlaybackDiagnostic("interactive_map_storage_load_failed", detail);
        }
      }
    )
      .then(({ text: csvText, attempt }) => {
        if (cancelled) return;
        const parsed = parseCSV(csvText);
  
        const nodes = {};
        parsed.forEach(row => {
          const startId = row.start_node_id;
          const endId   = row.end_node_id;
          const geom    = row.geometry
            .replace(/LINESTRING\s*\(/, '')
            .replace(/\)$/, '');
          const coordsArr = geom
            .split(',')
            .map(s => s.trim().split(' ').map(Number));
  
          if (coordsArr.length) {
            const [lng0, lat0] = coordsArr[0];
            const [lngN, latN] = coordsArr[coordsArr.length - 1];
            nodes[startId] = { id: startId, lat: lat0, lng: lng0 };
            nodes[endId]   = { id: endId,   lat: latN, lng: lngN };
          }
        });
  
        const adjList = {};
        parsed.forEach(row => {
          const start  = row.start_node_id;
          const end    = row.end_node_id;
          const length = parseFloat(row.length);
  
          if (!adjList[start]) adjList[start] = [];
          if (!adjList[end])   adjList[end]   = [];
  
          adjList[start].push({ node: end, weight: length, link: row });
        });

        console.log(parsed)
  
        setGraphData({ links: parsed, nodes, adjList });

        // Keep global WKT lookup in sync for storyboard
        const linkGeoms = {};
        parsed.forEach(row => {
          linkGeoms[row.link_id] = row.geometry;
        });
        linkGeomsRef.current = linkGeoms;
        setSimulationDataFailures((current) => ({ ...current, links: "" }));
        if (attempt > 1) {
          logRoutePlaybackDiagnostic("interactive_map_storage_load_recovered", `${asset}:attempt_${attempt}`);
        }
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Error fetching CSV after retries:', error);
        setSimulationDataFailures((current) => ({ ...current, links: storageErrorName(error) }));
      })
      .finally(() => {
        if (!cancelled) setSimulationDataLoading((current) => ({ ...current, links: false }));
      });

    return () => { cancelled = true; };
  }, [selectedSimulation, simulationDataRetryKey, logRoutePlaybackDiagnostic]);

  useEffect(() => {
    const fetchSimulations = async () => {
      try {
        const simsCol = collection(firestore, "simulations");
        const simDocs = await getDocs(simsCol);
        const simList = simDocs.docs.map(d => ({ id: d.id, ...d.data() }));
        setSimulations(simList);
  
        const desired = String(podcastConfig?.simulation || "");
        if (simList.length) {
          const match = simList.find(s => s.id === desired || s.name === desired);
          setSelectedSimulation(match || simList[simList.length - 1]);
        }
      } catch (err) {
        console.error("Error fetching simulations:", err);
      }
    };
  
    if (podcastId && podcastConfig) fetchSimulations();
  }, [podcastId, podcastConfig]);

  useEffect(() => {
    if (!selectedSimulation) return;                       // need the ID
    let cancelled = false;
    setDestinationNodes([]);
    setSimulationDataLoading((current) => ({ ...current, nodes: true }));

    const asset = "nodes_csv";
    fetchStorageText(
      `${selectedSimulation.id}/inputs/berkeley_nodes.csv`,
      (error, attempt, maxAttempts) => {
        const detail = `${asset}:attempt_${attempt}:${storageErrorName(error)}`;
        console.warn(`Simulation ${asset} load attempt ${attempt}/${maxAttempts} failed.`, error);
        logRoutePlaybackDiagnostic("interactive_map_storage_load_error", detail);
        if (attempt === maxAttempts) {
          logRoutePlaybackDiagnostic("interactive_map_storage_load_failed", detail);
        }
      }
    )
      .then(({ text: csv, attempt }) => {
        if (cancelled) return;
        const rows = parseCSV(csv);                        // you already have parseCSV
        const dests = rows
          .filter(r => r.type === 'destination')
          .map(r => ({ nodeId: r.node_id, name: r.name || r.node_id }));
        setDestinationNodes(dests);                        // update state
        setSimulationDataFailures((current) => ({ ...current, nodes: "" }));
        if (attempt > 1) {
          logRoutePlaybackDiagnostic("interactive_map_storage_load_recovered", `${asset}:attempt_${attempt}`);
        }
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Error loading nodes CSV after retries:', error);
        setSimulationDataFailures((current) => ({ ...current, nodes: storageErrorName(error) }));
      })
      .finally(() => {
        if (!cancelled) setSimulationDataLoading((current) => ({ ...current, nodes: false }));
      });
    return () => { cancelled = true; };
  }, [selectedSimulation, simulationDataRetryKey, logRoutePlaybackDiagnostic]);
  

useEffect(() => {
  if (selectedTab !== "routes") {
    STOP_ANIMS();    
    updateRouteOpacity(null);
    hideAllStoryboardOverlays();
    lastStoryboardCameraRef.current = null;
    setIsPodcastPlaying(false);
    setSafetyCalloutsVisible(true);
    setBalloonsSpec(null);
    setActiveCallout(null);
  }
}, [selectedTab]);

  // CSV parser helper.
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    const header = lines[0].split(',');
    const data = [];
    const regex = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;
    for (let i = 1; i < lines.length; i++) {
      let row = lines[i].split(regex);
      const obj = {};
      header.forEach((col, index) => {
        obj[col] = row[index] ? row[index].replace(/^"|"$/g, '') : '';
      });
      data.push(obj);
    }
    return data;
  };

  const handleGetTravelExperience = async () => {
    if (!selectedLocation) {
      window.alert(
        addressFlowErrors.selectMapStartPoint || 'Click on the map first to choose your starting point.'
      );
      return;
    }

    console.log("Here graphdata2", graphData)
  
    try {
      const exp = await getTravelExperiencesV2({
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        graphData,
        destinationNodes,
        selectedTime: Number(time),
        simulation: selectedSimulation,
        apiAbortSignal: apiCallControllerRef.current?.signal
      });
      console.log('Travel experience log:', exp);
      setTravelExperiences(exp);

      const narrative = buildTravelNarrativeV2(exp, selectedLocation, Number(time));
      setTravelExpNarrativeStream(narrative);

      
      /* 
      
      const uploadPoint = Date.now();
  
      await getTravelExperienceEnglishSummary(
        exp,
        selectedLocation,
        Number(time),
        uploadPoint
      );


      const db = getDatabase();
      const evac_experience_content_ref = ref(
        db,
        `/generated_narrative/${uploadPoint}`
      );
      onValue(evac_experience_content_ref, (snapshot) => {
        const val = snapshot.val();
        console.log(val)
        if (val && typeof val === "string") {
          setTravelExpNarrativeStream(val);       // reuse existing audio/text display
        }
      });
      */

    } catch (err) {
      console.error('Could not get travel experience', err);
    }
  };

  const handlePlayAgain = () => {
    setShowNextSteps(false);           // close the dialog
    clearPauseTimer();                 // clear any pending pause logic
    logPodcastAction({
      platform: isMobile?'mobile':'web',
      action: 'interactive_map_audio_replay',
      cta: '',
      node: String(nearestNodeIdRef.current || ''),
      chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
      param_lang: selectedLanguage,
      podcast: selectedSimulation?.name || ''
    });
    const a = routesAudioRef.current;
    if (a) {
      a.currentTime = 0;               // restart from the beginning
      playWithDisclaimer({ restart: true }).catch(() => {});
    }
  };
 

  // Function to play podcast audio.
  const playSection = (startTime) => {
    if (podcastAudioRef.current) {
      podcastAudioRef.current.currentTime = startTime;
      podcastAudioRef.current.play();
    }
  };

  const playRouteSection = (startTime) => {
    if (routesAudioRef.current) {
      routesAudioRef.current.currentTime = startTime;
      routesAudioRef.current.play();
    }
  };

  const createTextOverlay = (position, text) => {
    const overlay = new window.google.maps.OverlayView();
    overlay.onAdd = function() {
      this.div = document.createElement("div");
      // Style the overlay div as desired:
      this.div.style.position = "absolute";
      this.div.style.background = "rgba(255, 255, 255, 0.9)";
      this.div.style.color = "#000";
      this.div.style.padding = "2px 4px";
      this.div.style.borderRadius = "4px";
      this.div.style.fontSize = "16px";
      this.div.innerHTML = text;
      const panes = this.getPanes();
      panes.floatPane.appendChild(this.div);
    };
    overlay.draw = function() {
      const projection = this.getProjection();
      // Convert the geographical position to pixel coordinates.
      const point = projection.fromLatLngToDivPixel(new window.google.maps.LatLng(position.lat, position.lng));
      if (point && this.div) {
        // Adjust the position so the overlay appears centered above the marker.
        this.div.style.left = (point.x - this.div.offsetWidth / 2) + "px";
        this.div.style.top = (point.y - this.div.offsetHeight - 20) + "px"; // 5px above
      }
    };
    overlay.onRemove = function() {
      if (this.div) {
        this.div.parentNode.removeChild(this.div);
        this.div = null;
      }
    };
    overlay.setMap(mapRef.current);
    return overlay;
  };

  const apiCallControllerRef = useRef(null);

  const drawRoutes = async (lat, lng, selectedTime, selectedSimulation, dests) => {
    setCanShowNextStepsForSelectedNode(false);
    if (!dests || dests.length === 0) return;
  
    // Abort any ongoing calls
    if (apiCallControllerRef.current) apiCallControllerRef.current.abort();
    apiCallControllerRef.current = new AbortController();
  
    // Location marker
    if (circleRef.current) circleRef.current.setMap(null);
    const locationMarker = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapRef.current,
      icon: GET_HOUSE_MARKER_ICON(),
    });
    circleRef.current = locationMarker;
  
    // Clear old overlays
    STOP_ANIMS(); 
    polylineRefs.current.forEach(poly => poly.setMap(null));
    polylineRefs.current = [];
    carMarkersRef.current.forEach(marker => marker.setMap(null));
    carMarkersRef.current = [];
    additionalHouseMarkersRef.current.forEach(marker => marker.setMap(null));
    additionalHouseMarkersRef.current = [];
    routesDataRef.current = [];
    if (safetyCalloutsRef.current && safetyCalloutsRef.current.length > 0) {
      safetyCalloutsRef.current.forEach(callout => callout.setMap(null));
      safetyCalloutsRef.current = [];
    }
    if (sceneTimeOverlaysRef.current && sceneTimeOverlaysRef.current.length > 0) {
        sceneTimeOverlaysRef.current.forEach(ov => ov && ov.setMap(null));
        sceneTimeOverlaysRef.current = [];
      }
  
    if (!graphData.nodes || Object.keys(graphData.nodes).length === 0) {
      wantRoutesAutoplayRef.current = false;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "graph_unavailable");
      return;
    }
  
    // Build link geometry lookup
    const linkGeoms = {};
    (graphData.links || []).forEach(row => { linkGeoms[row.link_id] = row.geometry; });
    linkGeomsRef.current = linkGeoms;
  
    // Find nearest node to click
    const { nearestNodeId } = pickNearestGraphNode({
            lat,
            lng,
            graphData,
            k: 6,
            podcastNodeIds,
            // use your link length as weight (already in your adjList edges)
            weightAccessor: (edge) => Number(edge.weight || edge.link?.length || 0),
            // optional street tie-breakers using your existing helpers
            streetText: extractStreetFromInput(searchText),
            linkStreetName,
            streetSimilarity,
          });

      console.log("Nearest node", nearestNodeId)

    if (nearestNodeId == null) {
      wantRoutesAutoplayRef.current = false;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "nearest_node_unavailable", "");
      return;
    }

    nearestNodeIdRef.current = nearestNodeId;
    setSelectedNearestNodeId(String(nearestNodeId));

    // ⬇️ NEW: snap selectedLocation + marker to the nearest node
    const nn =
    graphData.nodes[nearestNodeId] || graphData.nodes[String(nearestNodeId)];
    if (nn) {
    setSelectedLocation({ lat: nn.lat, lng: nn.lng });
    if (circleRef.current) {
      circleRef.current.setPosition({ lat: nn.lat, lng: nn.lng });
    }
    }

    clearRoutesPodcast();
    let bundleLoadedForNode = false;
    try {
      await loadRouteBundleFromFirestore(nearestNodeId, selectedLanguage);
      bundleLoadedForNode = true;
    } catch (e) {
      console.error("Failed to load route bundle:", e);
    }

    const routeRows = await loadRouteTableForNode(nearestNodeId);
  
    const allowedDestIds = new Set(
      (routeRows || [])
        .map(row => String(row?.id ?? '').trim())
        .filter(Boolean)
    );
  
    if (allowedDestIds.size === 0) {
      wantRoutesAutoplayRef.current = false;
      console.warn("No route tables available for this starting node; skipping route draw.");
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "route_table_unavailable", nearestNodeId);
      return;
    }
  
    const filteredDests = (dests || []).filter(dest => {
      const id = String(dest?.nodeId ?? '').trim();
      return id && allowedDestIds.has(id);
    });
  
    if (filteredDests.length === 0) {
      wantRoutesAutoplayRef.current = false;
      console.warn("Route tables exist but none of their destinations match the simulation nodes.");
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "route_destinations_unavailable", nearestNodeId);
      return;
    }
  
    const savedRouteRows = (routeRows || [])
      .map((row) => {
        const nodeId = String(row?.id ?? "").trim();
        if (!nodeId || !allowedDestIds.has(nodeId)) return null;
        const orderedLinks =
          Array.isArray(row?.queuedVehiclesOnTime) && row.queuedVehiclesOnTime.length
            ? row.queuedVehiclesOnTime
            : Array.isArray(row?.queuedVehiclesLate)
            ? row.queuedVehiclesLate
            : [];
        const linkIds = orderedLinks
          .map((item) => item?.linkId)
          .filter((linkId) => linkId != null)
          .map((linkId) => String(linkId));
        if (!linkIds.length) return null;
        return {
          nodeId,
          link_ids: linkIds,
          source: "routeTable"
        };
      })
      .filter(Boolean);

    const selectedRoutes = savedRouteRows.length > 0
      ? savedRouteRows
      : selectTwoDiverseRoutesSimple({
          graphData,
          nearestNodeId,
          dests: filteredDests,
          selectedTime
        });
    if (!selectedRoutes || selectedRoutes.length === 0) {
      wantRoutesAutoplayRef.current = false;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "routes_unavailable", nearestNodeId);
      return;
    }

    setCanShowNextStepsForSelectedNode(true);
  
    // ---- Everything below remains the same, but iterate over selectedRoutes instead of dests ----
    const routesData = [];
  
    for (const sel of selectedRoutes) {
      const pathLinks = sel.link_ids || [];
      const routeCoords = Array.isArray(sel.node_path) && sel.node_path.length
        ? sel.node_path
            .map(nodeId => {
              const node = graphData.nodes[nodeId];
              return node ? { lat: node.lat, lng: node.lng } : null;
            })
            .filter(Boolean)
        : pathLinks.reduce((coords, linkId) => {
            const wkt = linkGeoms[linkId];
            if (!wkt) return coords;
            const linkCoords = wkt
              .replace(/^LINESTRING\s*\(/i, '')
              .replace(/\)$/, '')
              .split(',')
              .map(pt => {
                const [lng2, lat2] = pt.trim().split(' ').map(Number);
                return { lat: lat2, lng: lng2 };
              })
              .filter(pt => Number.isFinite(pt.lat) && Number.isFinite(pt.lng));
            if (!coords.length) return linkCoords;
            return coords.concat(linkCoords.slice(1));
          }, []);
      if (!routeCoords.length) continue;
  
      // Draw true geometry per link
      const routePolylines = pathLinks.map(linkId => {
        const wkt = linkGeoms[linkId];
        if (!wkt) return null;
        const coords = wkt
          .replace(/^LINESTRING\s*\(/i, '')
          .replace(/\)$/, '')
          .split(',')
          .map(pt => {
            const [lng2, lat2] = pt.trim().split(' ').map(Number);
            return { lat: lat2, lng: lng2 };
          });
  
        return new window.google.maps.Polyline({
          path: coords,
          geodesic: true,
          strokeColor: "#186EE3",
          strokeOpacity: 0.35,
          strokeWeight: 5,
          zIndex: -1000,
          clickable: true
        });
      }).filter(Boolean);
  
      // Put on map & wire clicks
      routePolylines.forEach((poly, idx) => {
        poly.setMap(mapRef.current);
        const linkId = pathLinks[idx];
        poly.addListener("click", () => console.log(`Link ID: ${linkId}`));
        polylineRefs.current.push(poly);
      });
  
      // Destination marker
      const destinationPos = routeCoords[routeCoords.length - 1];
      const destMarker = new window.google.maps.Marker({
        position: destinationPos,
        map: mapRef.current,
        icon: { url: SafetyIcon, scaledSize: new window.google.maps.Size(40, 40), anchor: new window.google.maps.Point(20, 20) },
        opacity: 1
      });
      destMarker.addListener('click', () => console.log(`Destination node ID: ${sel.nodeId}`));


      // Hospital marker
      if (!hospitalMarkerRef.current) {

        let marker = new window.google.maps.Marker({
          position: { lat: 37.715885, lng: -122.130340 },
          map: mapRef.current,
          icon: {
            url: HospitalIcon,
            scaledSize: new window.google.maps.Size(48, 48),
            anchor: new window.google.maps.Point(24, 24),
          },
          title: "Hospital",
        });

        hospitalMarkerRef.current = marker
  
      } else {
        hospitalMarkerRef.current.setMap(mapRef.current);
      }
  
      // Store for API update
      routesData.push({
        destNodeId: sel.nodeId,
        pathLinks,
        routePolylines,
        destinationPos,
        destMarker
      });
    }
  
    routesDataRef.current = routesData;

    const connectorRoutes = buildFreeFlowConnectorRoutes({
      adjList: graphData.adjList,
      links: graphData.links,
      primaryLinkIds: routesData.flatMap((route) => route.pathLinks || []),
      householdNodeIds: additionalHouseholdNodesRef.current,
    });
    connectorRoutes.forEach((connector) => {
      const houseNode = graphData.nodes?.[connector.nodeId];
      if (houseNode) {
        const marker = new window.google.maps.Marker({
          position: { lat: houseNode.lat, lng: houseNode.lng },
          map: mapRef.current,
          icon: GET_HOUSE_MARKER_ICON(),
          title: `Additional household node ${connector.nodeId}`,
        });
        additionalHouseMarkersRef.current.push(marker);
      }
      connector.linkIds.forEach((linkId) => {
        const wkt = linkGeoms[linkId];
        if (!wkt) return;
        const coords = wkt
          .replace(/^LINESTRING\s*\(/i, "")
          .replace(/\)$/, "")
          .split(",")
          .map((point) => {
            const [lng2, lat2] = point.trim().split(/\s+/).map(Number);
            return { lat: lat2, lng: lng2 };
          })
          .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));
        const polyline = new window.google.maps.Polyline({
          path: coords,
          geodesic: true,
          strokeColor: "#186EE3",
          strokeOpacity: 0.9,
          strokeWeight: 5,
          zIndex: -900,
          clickable: false,
        });
        polyline.setMap(mapRef.current);
        polylineRefs.current.push(polyline);
        START_LINK_ANIM(polyline, "#186EE3", 5, linkId);
      });
    });

    const chosenNodeId = nearestNodeId;
    let bundleLoaded = bundleLoadedForNode;
    if (chosenNodeId && !bundleLoaded) {
      try {
        await loadRouteBundleFromFirestore(chosenNodeId, selectedLanguage);
        bundleLoaded = true;
      } catch (e) {
        console.error("Failed to load route bundle:", e);
      }
    }

    if (!bundleLoaded) {
      wantRoutesAutoplayRef.current = false;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "media_bundle_load_failed", chosenNodeId);
    } else if (routesData.length === 0) {
      wantRoutesAutoplayRef.current = false;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "route_geometry_unavailable", chosenNodeId);
    } else {
      logRoutePlaybackDiagnostic("interactive_map_route_setup_succeeded", "route_and_media_ready", chosenNodeId);
    }

    // onCanPlay normally owns autoplay; this covers already-ready media.
    if (bundleLoaded && routesData.length > 0 && routesAudioRef.current && routesAudioUrl) {
      void attemptRoutesAutoplay("routes_draw_complete");
    }
  
    // Fire API calls concurrently (unchanged)
     routesData.forEach((routeData) => {
         const destId = String(routeData.destNodeId);
         const qmap = onTimeQueuesByDestRef.current.get(destId) || new Map();
      
         // Per-link animations + queues
         routeData.pathLinks.forEach((linkId, idx) => {
           const poly = routeData.routePolylines[idx];
           if (!poly) return;
           const queuedCount = Number(qmap.get(String(linkId)) || 0);
           // Animate link (free-flow dots). No per-link travel time, so use a steady pace.
           START_LINK_ANIM(poly, "#186EE3", 5, linkId);
           // Queue dots from on-time data
           SET_LINK_QUEUE(poly, queuedCount, 4, undefined, { animate: false, duration: 0 });
         });
      
         // Destination total time label from "leaveOnTime"
         const totalSec = Number(onTimeTotalsByDestRef.current.get(destId) || 0);
         const label = totalSec
           ? formatTime(totalSec, { style: "digital" })
           : (routeTableRows.find(r => String(r.id) === destId)?.leaveOnTime || "");
      
         const textOverlay = createTextOverlay(routeData.destinationPos, label);
         const isPlayingNow = !!routesAudioRef.current && !routesAudioRef.current.paused;
         if (isPlayingNow || !showRouteTimesWhenPausedRef.current) textOverlay.setMap(null);
         safetyCalloutsRef.current.push(textOverlay);
      });
  };

  const finishAddressEntryFlow = async (addressLabel = "") => {
    if (addressLabel) setResolvedAddressLabel(addressLabel);
    setAddressEntryMode("ready");
    await new Promise((resolve) => setTimeout(resolve, 650));
    if (autoplayOutcomeRef.current === "blocked") return;
    setShowAddressEntryFlow(false);
    setAddressEntryMode("form");
    setAddressSuggestions([]);
    setAddressEntryError("");
    setIsPickOnMapMode(false);
  };

  const applyLocationSelection = async (lat, lng, { addressLabel = "", logEndSearch = false, ctaValue = "" } = {}) => {
    const latNum = Number(lat);
    const lngNum = Number(lng);

    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
      throw new Error("Could not find this location.");
    }

    clearRoutesPodcast();
    setActiveCallout(null);
    setIsPodcastPlaying(false);

    setSelectedLocation({ lat: latNum, lng: lngNum });

    if (logEndSearch) {
      logPodcastAction({
        platform: isMobile ? "mobile" : "web",
        action: "location_search_end",
        cta: ctaValue,
        chapter: String(String(nearestNodeIdRef.current || "") + "_" + selectedLanguage),
        param_lang: selectedLanguage,
        podcast: selectedSimulation?.name || ""
      });
    }

    if (mapRef.current) {
      modifyMapCamera(
        mapRef.current,
        { lat: latNum, lng: lngNum },
        defaultZoom,
        1000
      );
    }

    if (selectedSimulation) {
      wantRoutesAutoplayRef.current = true;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_started", "address_selected");
      await drawRoutes(latNum, lngNum, time, selectedSimulation, destinationNodes);
    } else {
      wantRoutesAutoplayRef.current = false;
      logRoutePlaybackDiagnostic("interactive_map_route_setup_failed", "simulation_unavailable");
    }

    await finishAddressEntryFlow(addressLabel);
  };

  const firstActiveNodeId = String(activeNodes[0] || "").trim();
  const firstActiveNode = graphData?.nodes?.[firstActiveNodeId];
  const singleLocationReady =
    activeNodesForLanguage === String(selectedLanguage || "EN").toUpperCase() &&
    mapReady &&
    Boolean(selectedSimulation) &&
    destinationNodes.length > 0 &&
    Boolean(firstActiveNode);
  const hasSimulationDataFailure = Boolean(simulationDataFailures.links || simulationDataFailures.nodes);
  const isSimulationDataLoading = simulationDataLoading.links || simulationDataLoading.nodes;
  const singleLocationCtaDisabled = isSearchingLoc || (hasSimulationDataFailure
    ? isSimulationDataLoading
    : !singleLocationReady);

  const retrySimulationData = () => {
    const failedAssets = Object.entries(simulationDataFailures)
      .filter(([, error]) => Boolean(error))
      .map(([asset]) => `${asset}_csv`)
      .join(",");
    logRoutePlaybackDiagnostic("interactive_map_storage_retry_requested", failedAssets);
    setSimulationDataRetryKey((value) => value + 1);
  };

  const handleSingleLocationStart = async ({ trackCta = true } = {}) => {
    if (isSearchingLoc || !isSingleLocation) return;

    if (trackCta) {
      logPodcastAction({
        platform: isMobile ? "mobile" : "web",
        action: "single_location_audio_guide_cta_click",
        cta: "start_audio_guide",
        node: firstActiveNodeId,
        chapter: `${firstActiveNodeId}_${selectedLanguage}`,
        param_lang: selectedLanguage,
        podcast: selectedSimulation?.name || String(podcastId || "")
      });
    }

    if (!firstActiveNodeId) {
      setAddressEntryError("This single-location podcast has no active node configured.");
      return;
    }
    if (!firstActiveNode) {
      setAddressEntryError(
        `Active node ${firstActiveNodeId} was not found in the selected simulation.`
      );
      return;
    }
    if (!singleLocationReady) {
      setAddressEntryError("The audio guide is still loading. Please try again in a moment.");
      return;
    }

    setIsSearchingLoc(true);
    setAddressEntryMode("loading");
    setAddressEntryError("");
    setResolvedAddressLabel("");
    setAutoplayOutcome("pending");
    autoplayOutcomeRef.current = "pending";

    try {
      await applyLocationSelection(firstActiveNode.lat, firstActiveNode.lng, {
        addressLabel: "",
        ctaValue: "single_location"
      });
    } catch (err) {
      console.error("Failed to initialize single-location podcast:", err);
      setAddressEntryMode("form");
      setAddressEntryError("Could not load the configured single-location podcast.");
      setShowAddressEntryFlow(true);
    } finally {
      setIsSearchingLoc(false);
    }
  };

  const demoAutoStartRef = useRef(false);
  useEffect(() => {
    if (!demoGateEnabled || demoAutoStartRef.current || !isSingleLocation || !singleLocationReady) return;
    demoAutoStartRef.current = true;
    setShowAddressEntryFlow(true);
    void handleSingleLocationStart({ trackCta: false });
    // The one-shot ref deliberately prevents reruns when the start handler is recreated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoGateEnabled, isSingleLocation, singleLocationReady]);

  const handleMapClick = async (e) => {
    // If the main podcast is playing, ignore clicks (don’t start a new location/routes)
    if (isSingleLocation || (routesAudioRef.current && !routesAudioRef.current.paused)) {
      return;
    }

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    const pickedLabel = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

    console.log("Map clicked at:", { lat, lng, time, selectedSimulation });

    if (isPickOnMapMode) {
      setShowAddressEntryFlow(true);
      setAddressEntryMode("loading");
      setAddressEntryError("");
    }

    try {
      await applyLocationSelection(lat, lng, { addressLabel: pickedLabel });
    } catch (err) {
      console.error("Map pick failed:", err);
      setAddressEntryMode("form");
      setAddressEntryError(
        addressFlowErrors.mapPointInvalid || "We could not use that map point. Try another location."
      );
      setShowAddressEntryFlow(true);
      setIsPickOnMapMode(true);
    } finally {
      setIsSearchingLoc(false);
    }
  };

  const handleUseMyLocation = () => {
    if (isSearchingLoc) return;

    if (!navigator.geolocation) {
      setAddressEntryError(
        addressFlowErrors.locationAccessUnavailable || "Location access is not available on this device."
      );
      return;
    }

    setAddressEntryError("");
    setAddressSuggestions([]);
    setAddressEntryMode("loading");
    setShowAddressEntryFlow(true);
    setIsSearchingLoc(true);

    logPodcastAction({
      platform: isMobile ? "mobile" : "web",
      action: "location_search_start",
      cta: "use_my_location",
      chapter: String(String(nearestNodeIdRef.current || "") + "_" + selectedLanguage),
      param_lang: selectedLanguage,
      podcast: selectedSimulation?.name || ""
    });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = Number(position.coords?.latitude);
          const lng = Number(position.coords?.longitude);
          await applyLocationSelection(lat, lng, {
            addressLabel: addressFlowText.currentLocationLabel || "Current location",
            logEndSearch: true,
            ctaValue: "use_my_location"
          });
        } catch (err) {
          console.error("Use my location failed:", err);
          setAddressEntryMode("form");
          setAddressEntryError(
            addressFlowErrors.currentLocationFailed || "Could not use your current location. Please enter an address."
          );
        } finally {
          setIsSearchingLoc(false);
        }
      },
      () => {
        setIsSearchingLoc(false);
        setAddressEntryMode("form");
        setAddressEntryError(
          addressFlowErrors.locationPermissionDenied || "Location permission denied. Please enter your address."
        );
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const handleStartPickOnMap = () => {
    if (isSearchingLoc) return;
    setAddressEntryError("");
    setAddressSuggestions([]);
    setIsPickOnMapMode(true);
    setShowAddressEntryFlow(false);
  };

  const handleSuggestionPick = (suggestion) => {
    if (!suggestion?.value) return;
    setSearchText(suggestion.value);
    setAddressSuggestions([]);
    setAddressEntryError("");
    handleLocationSearch(suggestion.value);
  };



  const handleTimeChange = (e) => { 
    const newT = Number(e.target.value);
    setTime(newT);
    if (selectedLocation && selectedSimulation) {
      drawRoutes(selectedLocation.lat, selectedLocation.lng, newT, selectedSimulation, destinationNodes);
    }

  };

  const togglePolylines = () => { setShowPolyLines(!showPolyLines); };

  const handleSimulationChange = (e) => {
    const simID = e.target.value;
    console.log(simID)
    const sim = simulations.find(s => s.id === simID);
    if (sim) setSelectedSimulation(sim);

    if (selectedLocation && selectedSimulation) {
       drawRoutes(selectedLocation.lat, selectedLocation.lng, time, sim, destinationNodes);
    }
  };

  const loadFireOverlay = () => {
    // Add the fire marker here:
    // Define the fixed center and the offset in meters
    const fireCenter = { lat: 37.891993946041026, lng: -122.24859240728762 };
    const meters = 400; // adjust this value: approximately 150 meters covers 3 blocks
    const meterToDegLat = 1 / 111000; // roughly 1° latitude = 111 km
    // For longitude, adjust by the cosine of the latitude.
    const meterToDegLng = 1 / (111000 * Math.cos(fireCenter.lat * Math.PI / 180));
    const latOffset = meters * meterToDegLat;
    const lngOffset = meters * meterToDegLng;

    // Create bounds for the GroundOverlay.
    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(fireCenter.lat - latOffset, fireCenter.lng - lngOffset),
      new window.google.maps.LatLng(fireCenter.lat + latOffset, fireCenter.lng + lngOffset)
    );

    // Create and display the GroundOverlay using your FireImage.
    new window.google.maps.GroundOverlay(FireImage, bounds, { opacity: 0.75 }).setMap(mapRef.current);
  }

  

  const audioSegments = React.useMemo(() => {
    const timings = routeTimings || {};
    const sorted = Object.entries(timings)
      .map(([title, start]) => ({ title, start }))
      .sort((a, b) => a.start - b.start);
    sorted.forEach((seg, i) => (seg.end = i < sorted.length - 1 ? sorted[i + 1].start : Infinity));
    return sorted;
  }, [routeTimings]);
  
  const nodeIdForTime = (t) => {
    const seg = audioSegments.find(s => t >= s.start && t < s.end);
    if (!seg) return null;
    const item = activeChecklistItems.find(i => i.title === seg.title);
    return item?.nodeId ?? null;
  };

  const updateRouteOpacity = (activeNodeId) => {
    routesDataRef.current.forEach(r => {
      const isActive = activeNodeId && String(r.destNodeId) === String(activeNodeId);
  
      // Polylines: keep all on map, dim non-active
      r.routePolylines.forEach(p => {
        p.setOptions({ strokeOpacity: isActive ? 1 : (activeNodeId ? 0.05 : 1) });
        if (p.getMap() === null) p.setMap(mapRef.current);
        SET_LINK_ANIM_VISIBLE(p, !!(isActive || !activeNodeId));
      });
  
      // Destination markers: ONLY show the active one when a node is active
      if (activeNodeId) {
        if (isActive) {
          if (!r.destMarker.getMap()) r.destMarker.setMap(mapRef.current);
          r.destMarker.setOpacity(1);
        } else {
          r.destMarker.setOpacity(0.05);
        }
      } else {
        // No active node: show all again
        if (!r.destMarker.getMap()) r.destMarker.setMap(mapRef.current);
        r.destMarker.setOpacity(1);
      }
    });
  };

  const handleLocationSearch = async (rawSearchText = searchText) => {
    if (isSearchingLoc) return false;

    const ctaValue = String(rawSearchText || "").trim();
    if (!ctaValue) return false;
    const query = ctaValue;

    setAddressEntryError("");
    autoplayOutcomeRef.current = "pending";
    setAutoplayOutcome("pending");
    setAddressSuggestions([]);
    setAddressEntryMode("loading");
    setShowAddressEntryFlow(true);
    setIsPickOnMapMode(false);
    clearRoutesPodcast();
    setActiveCallout(null);
    setIsPodcastPlaying(false);

    logPodcastAction({
      platform: isMobile ? "mobile" : "web",
      action: "location_search_start",
      cta: ctaValue,
      chapter: String(String(nearestNodeIdRef.current || "") + "_" + selectedLanguage),
      param_lang: selectedLanguage,
      podcast: selectedSimulation?.name || ""
    });

    try {
      setIsSearchingLoc(true);
      const { lat, lng } = await geocodeAddress(query, "", "");
      try {
        await applyLocationSelection(lat, lng, {
          addressLabel: ctaValue,
          logEndSearch: true,
          ctaValue
        });
      } catch (err) {
        console.error("Location was geocoded, but podcast route setup failed:", err);
        setAddressEntryMode("form");
        setShowAddressEntryFlow(true);
        setAddressEntryError(
          addressFlowErrors.podcastLoadFailed ||
            "We found that address, but could not load this podcast route. Check this podcast's media and route scene data."
        );
        return false;
      }
      return true;
    } catch (err) {
      console.error("Geocode failed:", err);
      setAddressEntryMode("form");
      setAddressEntryError(
        addressFlowErrors.addressNotFound || "We could not find that address. Try selecting from the suggestions."
      );
      return false;
    } finally {
      setIsSearchingLoc(false);
    }
  };

  const modifyMapCamera = (map, endCenter, endZoom, duration = 1000) => {
    const mapCenter = map?.getCenter?.();
    const startCenter = mapCenter?.toJSON?.();
    const startZoom   = map?.getZoom?.();
    const targetZoom = Number.isFinite(Number(endZoom)) ? Number(endZoom) : startZoom;
    if (
      !startCenter ||
      !Number.isFinite(Number(startCenter.lat)) ||
      !Number.isFinite(Number(startCenter.lng)) ||
      !Number.isFinite(Number(startZoom)) ||
      !Number.isFinite(Number(targetZoom))
    ) {
      map.setCenter(endCenter);
      if (Number.isFinite(Number(targetZoom))) map.setZoom(Number(targetZoom));
      return;
    }
    const frames      = Math.round((duration / 1000) * 60); // ~60 fps
    let frame = 0;
  
    const latDiff  = endCenter.lat - startCenter.lat;
    const lngDiff  = endCenter.lng - startCenter.lng;
    const zoomDiff = targetZoom    - startZoom;
  
    const step = () => {
      frame += 1;
      const t    = frame / frames;
      const ease = t < .5 ? 2 * t * t : -1 + (4 - 2 * t) * t;   // ease-in/out
  
      map.setCenter({
        lat: startCenter.lat + latDiff * ease,
        lng: startCenter.lng + lngDiff * ease
      });
      map.setZoom(startZoom + zoomDiff * ease);
  
      if (frame < frames) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  const canShowContinueCta =
    !demoGateEnabled &&
    canShowNextStepsForSelectedNode &&
    !!selectedLocation &&
    hasCrossedNextStepsThresholdRef.current &&
    !isPodcastPlaying &&
    !showNextSteps;

  const showRainOverlay = activeOverlays.includes("rain") || sceneFlooding;
  const RoutesMediaElement = routesMediaKind === "video" ? "video" : "audio";


  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh", overflow: "hidden" }}>


          <div style={{ position: "relative", /* NEW wrapper */ }}>
            <div
              style={{
                position: "absolute",
                top: 10, left: 0, right: 0,
                width: "90%", maxWidth: 500, margin: "0 auto",
                zIndex: 5
              }}
            >

            {!isPodcastPlaying && (
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
                <h1 className="page-title-gradient"
                style={{ whiteSpace: 'nowrap', fontFamily: '"Trebuchet MS", Verdana, Tahoma, Arial, sans-serif' }}>
                  {uiText.title}
                </h1>
              </div>
              )}
              {!isSingleLocation && !isPodcastPlaying && !showAddressEntryFlow && (
                <SearchBar
                  value={searchText}
                  onChange={handleSearchTextInput}
                  onSubmit={handleLocationSearch}
                  isLoading={isSearchingLoc}
                  placeholder={addressFlowText.searchBarPlaceholder || "Enter address and press Enter"}
                />
              )}

              {isDisclaimerPlaying && (
                <div
                  style={{
                    background: "#fff7ed",
                    color: "#9a3412",
                    border: "1px solid #fdba74",
                    borderRadius: 14,
                    padding: "12px 16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.12)",
                    fontSize: 18,
                    fontWeight: 800,
                    textAlign: "center"
                  }}
                >
                  Safety disclaimer
                </div>
              )}

              {!isDisclaimerPlaying && isPodcastPlaying && activeCallout && (
                  <div
                  style={{
                    background: activeCallout.bg || "#fff",
                    color: activeCallout.textColor || "#111",
                    borderRadius: 14,
                    padding: "12px 16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.12)",
                    fontSize: 18,         
                    lineHeight: 1.4,
                    fontFamily:
                      '"Segoe Print","Comic Sans MS","Bradley Hand","Apple Chancery","Caveat","Patrick Hand",cursive',
                    whiteSpace: "pre-line"
                  }}
                >
                  {activeCallout.text}
                </div>
                )}
            </div>

            <BalloonsOverlay
              show={!!balloonsSpec}
              count={balloonsSpec?.count ?? 15}
              emoji={balloonsSpec?.emoji ?? "🎈"}
              minDuration={balloonsSpec?.minDuration ?? 3.0}
              maxDuration={balloonsSpec?.maxDuration ?? 4.2}
              topBeyondVh={balloonsSpec?.topBeyondVh ?? 180}
            />

            <style>{`
            @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
            @keyframes heroRollIn {
              from { transform: translateX(-120%) rotate(-18deg); opacity: 0; }
              to   { transform: translateX(0) rotate(0deg); opacity: 1; }
            }
            @keyframes heroRollOut {
              from { transform: translateX(0) rotate(0deg); opacity: 1; }
              to   { transform: translateX(120%) rotate(18deg); opacity: 0; }
            }
            .heroFrameWrap { will-change: transform, opacity; }
            .hero-roll-in  { animation: heroRollIn 1500ms cubic-bezier(.2,.8,.2,1) both; }
            .hero-roll-out { animation: heroRollOut 1500ms cubic-bezier(.2,.8,.2,1) both; }
            `}</style>
            
            
            <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={GOOGLE_MAP_LIBRARIES}>
              <div className={isMapShaking ? "map-shake" : ""} style={{ width: "100%", height: "100%" }}>
                <GoogleMap
                  mapContainerStyle={containerStyle}
                  center={mapCenter}
                  zoom={defaultZoom}
                  onLoad={(map) => {
                    mapRef.current = map;
                    setMapReady(true);
                  }}
                  onZoomChanged={UPDATE_HOUSE_MARKER_SIZES}
                  onClick={handleMapClick}
                options={{
                  styles: darkMapStyle,
                  disableDefaultUI: true,
                  mapTypeControl: false,
                  zoomControl: false,
                  streetViewControl: false,
                  fullscreenControl: false,
                  scaleControl: false,
                  rotateControl: false,
                  keyboardShortcuts: false,
                  mapTypeId: currentMapTypeId
                }}
              >
                  {/* Render additional overlays, e.g., car markers via state */}
                  {carOverlays}
                </GoogleMap>
              </div>
            </LoadScript>
            {showRainOverlay && <RainOverlay intensity={3} showFlooding={sceneFlooding} />}

            <HeroSceneMedia
              activeHeroUrl={activeHeroUrl}
              activeHeroVideo={activeHeroVideo}
              heroAnim={heroAnim}
              heroVideoRef={heroVideoRef}
              heroVisible={heroVisible}
              isMobile={isMobile}
              isPodcastPlaying={isPodcastPlaying}
              onAnimationEnd={handleHeroAnimationEnd}
            />

            {isPickOnMapMode && !showAddressEntryFlow && !isPodcastPlaying && (
              <div
                style={{
                  position: "absolute",
                  top: 72,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 25,
                  background: "rgba(2, 6, 23, 0.88)",
                  color: "#f8fafc",
                  borderRadius: 999,
                  padding: "10px 16px",
                  fontSize: 14,
                  fontWeight: 700,
                  boxShadow: "0 10px 28px rgba(0,0,0,0.32)"
                }}
              >
                {addressFlowText.pickOnMapHint || "Click on the map to choose your address"}
              </div>
            )}

          

            {/* AUDIO BAR OVERLAY (bottom) */}
            {(routesAudioUrl || canShowNextStepsForSelectedNode) && selectedLocation && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 12,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 12,         
                  zIndex: 6
                }}
              >
                {captions.enabled && isPodcastPlaying && (
                  <PodcastCaptionOverlay text={captions.text} isMobile={isMobile} />
                )}
                {/* If playing: show only Pause; if not playing: show Next Steps (when allowed) + Play */}
                {isPodcastPlaying && routesAudioUrl ? (
                  <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                    <button
                      onClick={() =>
                      {
                        pauseActivePlayback()
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_pause',
                          cta: "",
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          podcast: selectedSimulation?.name || ''
                        });

                      }}
                      style={{
                        background: "#f97316",
                        color: "#111827",
                        border: "none",
                        padding: "18px 28px",
                        borderRadius: 14,
                        cursor: "pointer",
                        fontWeight: 800,
                        fontSize: 20,
                        boxShadow: "0 12px 28px rgba(0,0,0,.35)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12
                      }}
                      aria-label="Pause"
                      title="Pause"
                    >
                      {uiText.pause}
                    </button>
                    {captions.available && (
                      <PodcastCcButton enabled={captions.enabled} onToggle={captions.toggle} />
                    )}
                    {demoGateEnabled && (
                      <a
                        href="/"
                        style={{
                          background: "rgba(255,255,255,.95)",
                          color: "#334155",
                          padding: "18px 24px",
                          borderRadius: 14,
                          fontWeight: 800,
                          fontSize: 20,
                          boxShadow: "0 12px 28px rgba(0,0,0,.3)",
                          display: "inline-flex",
                          alignItems: "center",
                          textDecoration: "none"
                        }}
                      >
                        Exit
                      </a>
                    )}
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                    {/* Always offer Play when not playing and audio is available */}
                    {routesAudioUrl && !showNextSteps && (
                      <button
                        onClick={() => {
                          playWithDisclaimer().catch(() => {});
                          setIsPodcastPlaying(true);
                          logPodcastAction({ 
                            platform: isMobile?'mobile': 'web',
                            action: 'interactive_map_audio_play',
                            cta: '',
                            chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                            node: String(nearestNodeIdRef.current || ''),
                            param_lang: selectedLanguage,
                            podcast: selectedSimulation?.name || ''
                          });
                        }}
                        style={{
                          background: "#f97316",
                          color: "#111827",
                          border: "none",
                          padding: "18px 28px",
                          borderRadius: 14,
                          cursor: "pointer",
                          fontWeight: 800,
                          fontSize: 20,
                          boxShadow: "0 12px 28px rgba(0,0,0,.35)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 12
                        }}
                        aria-label="Play"
                        title="Play"
                      >
                        {uiText.play}
                      </button>
                    )}
                    {!demoGateEnabled && canShowNextStepsForSelectedNode && !showNextSteps && (
                      <button
                        onClick={() => {
                          logPodcastAction({ 
                            platform: isMobile ? 'mobile' : 'web', 
                            action: 'next_steps_cta_click', 
                            cta: 'continue_to_next_steps', 
                            node: String(nearestNodeIdRef.current || ''), 
                            chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage), 
                            param_lang: selectedLanguage, 
                            podcast: selectedSimulation?.name || '' 
                          });
                          openNextSteps('manual_cta');
                        }}
                        style={{
                          background: "#f97316",
                          color: "#111827",
                          border: "none",
                          padding: "18px 28px",
                          borderRadius: 14,
                          cursor: "pointer",
                          fontWeight: 800,
                          fontSize: 20,
                          boxShadow: "0 12px 28px rgba(0,0,0,.35)",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 12
                        }}
                        aria-label="Continue to Next Steps"
                        title="Continue to Next Steps"
                      >
                        <span>{uiText.nextSteps}</span>
                        <span style={{ fontSize: 22, lineHeight: 1 }}>→</span>
                      </button>
                    )}
                    
                  </div>
                )}

                {routesAudioUrl && (
                  <div
                    style={{
                      position: routesMediaKind === "video" ? "fixed" : undefined,
                      zIndex: routesMediaKind === "video" ? 5 : undefined,
                      top: routesMediaKind === "video" ? (isPodcastPlaying ? 76 : 80) : undefined,
                      bottom: routesMediaKind === "video" ? 112 : undefined,
                      left: routesMediaKind === "video" ? 0 : undefined,
                      right: routesMediaKind === "video" ? 0 : undefined,
                      display: routesMediaKind === "video" ? "grid" : undefined,
                      placeItems: routesMediaKind === "video" ? "center" : undefined,
                      pointerEvents: routesMediaKind === "video" ? "none" : undefined,
                      order: routesMediaKind === "video" ? undefined : 0,
                      width: routesMediaKind === "video" ? undefined : 720,
                      maxWidth: routesMediaKind === "video" ? undefined : "94vw",
                      aspectRatio: routesMediaKind === "video" ? undefined : undefined,
                      background: routesMediaKind === "video" ? "transparent" : "rgba(255,255,255,0.95)",
                      boxShadow: routesMediaKind === "video"
                        ? "none"
                        : "0 8px 24px rgba(0,0,0,.18), 0 2px 8px rgba(0,0,0,.12)",
                      borderRadius: routesMediaKind === "video" ? 0 : 12,
                      padding: routesMediaKind === "video" ? 0 : 8,
                      overflow: routesMediaKind === "video" ? "visible" : "hidden",
                      filter: routesMediaKind === "video" ? "drop-shadow(0 18px 40px rgba(0,0,0,.45))" : undefined,
                      opacity: routesMediaKind === "video" && routeVideoHiddenForCamera ? 0 : 1,
                      visibility: routesMediaKind === "video" && routeVideoHiddenForCamera ? "hidden" : "visible",
                      transition: routesMediaKind === "video" ? "opacity 180ms ease" : undefined
                    }}
                  >
                    {routesMediaKind === "video" ? (
                      <HeroFrame
                        style={{
                          width: isMobile ? "min(82vh, 90vw, 880px)" : "min(68vh, 68vw, 820px)",
                          maxWidth: "92vw"
                        }}
                      >
                    <RoutesMediaElement
                      controls={false}
                      src={routesAudioUrl}
                    ref={routesAudioRef}
                    playsInline
                    preload="metadata"
                    style={routesMediaKind === "video" ? {
                      display: "block",
                      width: "100%",
                      height: "100%",
                      borderRadius: "9999px",
                      background: "#000",
                      objectFit: "cover",
                      overflow: "hidden"
                    } : { width: "100%" }}
                    onLoadStart={(event) => logRouteMediaDiagnostic("interactive_map_media_load_started", event.currentTarget)}
                    onError={(event) => logRouteMediaDiagnostic("interactive_map_media_error", event.currentTarget)}
                    onCanPlay={(e) => {
                      logRouteMediaDiagnostic("interactive_map_media_can_play", e.currentTarget);
                      e.currentTarget.playbackRate = 1;
                      clearPauseTimer();

                      const currSrc = e.currentTarget.currentSrc || e.currentTarget.src;
                      if (currSrc !== lastAudioSrcRef.current) {
                        // New file / session -> reset once
                        hasCrossedNextStepsThresholdRef.current = false;
                        hasCrossed90Ref.current = false;
                        nextStepsShownRef.current = false;
                        lastAudioSrcRef.current = currSrc;
                      }

                      void attemptRoutesAutoplay("media_can_play");
                    }}
                    onPlay={(event) => {
                      if (gateMainPlayback(event.currentTarget)) return;
                      if (routesAudioRef.current) routesAudioRef.current.playbackRate = 1;
                      clearPauseTimer();
                      setIsPodcastPlaying(true);
                      setSafetyCalloutsVisible(false);
                      stopResourcePlayback();

                      if (!hasLoggedPlayRef.current) {
                        hasLoggedPlayRef.current = true;
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_play',
                          cta: '',
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          podcast: selectedSimulation?.name || ''
                        });
                      }
                    }}
                    onSeeking={() => {
                      isSeekingRef.current = true;
                      clearPauseTimer();
                    }}
                    
                    onSeeked={(event) => {
                      handleDemoSeeked(event.currentTarget);
                    }}
                    onPause={(e) => {
                      activeRouteNodeRef.current = null;
                      updateRouteOpacity(null);
                      hideAllStoryboardOverlays();
                      lastStoryboardCameraRef.current = null;
                      setIsPodcastPlaying(false);
                      setSafetyCalloutsVisible(true);
                      setActiveCallout(null);
                      setBalloonsSpec(null);
                      clearHeroMedia();
                    
                      clearPauseTimer();
                    
                      // Ignore pauses caused by scrubbing
                      if (isSeekingRef.current || e.currentTarget.seeking) return;

                      if (handleDemoPause(e.currentTarget)) return;
                    
                      const t = e.currentTarget.currentTime;

                      logPodcastAction({
                        platform: isMobile?'mobile': 'web',
                        action: 'interactive_map_audio_pause',
                        cta: String(t),
                        node: String(nearestNodeIdRef.current || ''),
                        param_lang: selectedLanguage,
                        chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                        podcast: selectedSimulation?.name || ''
                      });
                    
                      if (t >= NEXT_STEPS_AUTO_SHOW_SECONDS && !nextStepsShownRef.current) {
                        // Confirm it's a real pause
                        pauseTimerRef.current = setTimeout(() => {
                          const a = routesAudioRef.current;
                          if (a && a.paused && !a.seeking) {
                            openNextSteps('auto_pause');
                            nextStepsShownRef.current = true; // <-- stays true across future seeks
                          }
                        }, 400);
                      }
                    }}
                    onEnded={() => {
                      logPodcastAction({
                        platform: isMobile?'mobile': 'web',
                        action: 'interactive_map_audio_ended',
                        cta: '',
                        node: String(nearestNodeIdRef.current || ''),
                        chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                        param_lang: selectedLanguage,
                        podcast: selectedSimulation?.name || ''
                      });

                      clearPauseTimer();
                      activeRouteNodeRef.current = null;
                      updateRouteOpacity(null);
                      hideAllStoryboardOverlays();
                      lastStoryboardCameraRef.current = null;
                      setIsPodcastPlaying(false);
                      setSafetyCalloutsVisible(true);
                      setActiveCallout(null);
                      setBalloonsSpec(null);
                      clearHeroMedia();
                      openNextSteps('auto_audio_end');
                      nextStepsShownRef.current = true; 
                    }}
                    onTimeUpdate={(e) => {
                      const t = e.currentTarget.currentTime;
                      captions.syncMain(t);
                      if (handleDemoTimeUpdate(e.currentTarget)) return;

                      if (t >= NEXT_STEPS_AUTO_SHOW_SECONDS && !hasCrossedNextStepsThresholdRef.current) {
                        hasCrossedNextStepsThresholdRef.current = true;
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_crossed_next_steps_threshold',
                          cta: '',
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          podcast: selectedSimulation?.name || ''
                        });
                      }

                      if (t >= NEXT_STEPS_ANALYTICS_SECONDS && !hasCrossed90Ref.current) {
                        hasCrossed90Ref.current = true;
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_crossed_90_seconds',
                          cta: '',
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          podcast: selectedSimulation?.name || ''
                        });
                      }
                      
                      if (t >= NEXT_STEPS_AUTO_SHOW_SECONDS && !nextStepsShownRef.current) {
                        hasCrossedNextStepsThresholdRef.current = true;
                      }

                      const node = nodeIdForTime(t);
                      if (node !== activeRouteNodeRef.current) {
                        activeRouteNodeRef.current = node;
                        updateRouteOpacity(node);
                      }
                      applyStoryboardForTime(t);
                    }}
                    />
                      </HeroFrame>
                    ) : (
                    <RoutesMediaElement
                      controls
                      src={routesAudioUrl}
                    ref={routesAudioRef}
                    playsInline
                    preload="metadata"
                    style={{ width: "100%" }}
                    onLoadStart={(event) => logRouteMediaDiagnostic("interactive_map_media_load_started", event.currentTarget)}
                    onError={(event) => logRouteMediaDiagnostic("interactive_map_media_error", event.currentTarget)}
                    onCanPlay={(e) => {
                      logRouteMediaDiagnostic("interactive_map_media_can_play", e.currentTarget);
                      e.currentTarget.playbackRate = 1;
                      clearPauseTimer();

                      const currSrc = e.currentTarget.currentSrc || e.currentTarget.src;
                      if (currSrc !== lastAudioSrcRef.current) {
                        // New file / session -> reset once
                        hasCrossedNextStepsThresholdRef.current = false;
                        hasCrossed90Ref.current = false;
                        nextStepsShownRef.current = false;
                        lastAudioSrcRef.current = currSrc;
                      }

                      void attemptRoutesAutoplay("media_can_play");
                    }}
                    onPlay={(event) => {
                      if (gateMainPlayback(event.currentTarget)) return;
                      if (routesAudioRef.current) routesAudioRef.current.playbackRate = 1;
                      clearPauseTimer();
                      setIsPodcastPlaying(true);
                      setSafetyCalloutsVisible(false);
                      stopResourcePlayback();

                      if (!hasLoggedPlayRef.current) {
                        hasLoggedPlayRef.current = true;
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_play',
                          cta: '',
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          podcast: selectedSimulation?.name || ''
                        });
                      }
                    }}
                    onSeeking={() => {
                      isSeekingRef.current = true;
                      clearPauseTimer();
                    }}
                    
                    onSeeked={(event) => {
                      handleDemoSeeked(event.currentTarget);
                    }}
                    onPause={(e) => {
                      activeRouteNodeRef.current = null;
                      updateRouteOpacity(null);
                      hideAllStoryboardOverlays();
                      lastStoryboardCameraRef.current = null;
                      setIsPodcastPlaying(false);
                      setSafetyCalloutsVisible(true);
                      setActiveCallout(null);
                      setBalloonsSpec(null);
                      clearHeroMedia();
                    
                      clearPauseTimer();
                    
                      // Ignore pauses caused by scrubbing
                      if (isSeekingRef.current || e.currentTarget.seeking) return;

                      if (handleDemoPause(e.currentTarget)) return;
                    
                      const t = e.currentTarget.currentTime;

                      logPodcastAction({
                        platform: isMobile?'mobile': 'web',
                        action: 'interactive_map_audio_pause',
                        cta: String(t),
                        node: String(nearestNodeIdRef.current || ''),
                        param_lang: selectedLanguage,
                        chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                        podcast: selectedSimulation?.name || ''
                      });
                    
                      if (t >= NEXT_STEPS_AUTO_SHOW_SECONDS && !nextStepsShownRef.current) {
                        // Confirm it's a real pause
                        pauseTimerRef.current = setTimeout(() => {
                          const a = routesAudioRef.current;
                          if (a && a.paused && !a.seeking) {
                            openNextSteps('auto_pause');
                            nextStepsShownRef.current = true; // <-- stays true across future seeks
                          }
                        }, 400);
                      }
                    }}
                    onEnded={() => {
                      logPodcastAction({
                        platform: isMobile?'mobile': 'web',
                        action: 'interactive_map_audio_ended',
                        cta: '',
                        node: String(nearestNodeIdRef.current || ''),
                        chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                        param_lang: selectedLanguage,
                        podcast: selectedSimulation?.name || ''
                      });

                      clearPauseTimer();
                      activeRouteNodeRef.current = null;
                      updateRouteOpacity(null);
                      hideAllStoryboardOverlays();
                      lastStoryboardCameraRef.current = null;
                      setIsPodcastPlaying(false);
                      setSafetyCalloutsVisible(true);
                      setActiveCallout(null);
                      setBalloonsSpec(null);
                      clearHeroMedia();
                      openNextSteps('auto_audio_end');
                      nextStepsShownRef.current = true; 
                    }}
                    onTimeUpdate={(e) => {
                      const t = e.currentTarget.currentTime;
                      captions.syncMain(t);
                      if (handleDemoTimeUpdate(e.currentTarget)) return;

                      if (t >= NEXT_STEPS_AUTO_SHOW_SECONDS && !hasCrossedNextStepsThresholdRef.current) {
                        hasCrossedNextStepsThresholdRef.current = true;
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_crossed_next_steps_threshold',
                          cta: '',
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          podcast: selectedSimulation?.name || ''
                        });
                      }

                      if (t >= NEXT_STEPS_ANALYTICS_SECONDS && !hasCrossed90Ref.current) {
                        hasCrossed90Ref.current = true;
                        logPodcastAction({
                          platform: isMobile?'mobile': 'web',
                          action: 'interactive_map_audio_crossed_90_seconds',
                          cta: '',
                          node: String(nearestNodeIdRef.current || ''),
                          param_lang: selectedLanguage,
                          chapter: String(String(nearestNodeIdRef.current || '') + "_"+ selectedLanguage),
                          podcast: selectedSimulation?.name || ''
                        });
                      }
                      
                      if (t >= NEXT_STEPS_AUTO_SHOW_SECONDS && !nextStepsShownRef.current) {
                        hasCrossedNextStepsThresholdRef.current = true;
                      }

                      const node = nodeIdForTime(t);
                      if (node !== activeRouteNodeRef.current) {
                        activeRouteNodeRef.current = node;
                        updateRouteOpacity(node);
                      }
                      applyStoryboardForTime(t);
                    }}
                    />
                    )}
                  </div>
                )}
              </div>
            )}


            <audio
              ref={disclaimerAudioRef}
              src={disclaimerAudioUrl}
              preload="auto"
              onTimeUpdate={(event) => captions.syncDisclaimer(event.currentTarget.currentTime)}
              onEnded={() => {
                captions.clearDisclaimer();
                handleDisclaimerEnded();
              }}
              onPlay={() => setIsPodcastPlaying(true)}
              onPause={(event) => {
                if (!event.currentTarget.ended && disclaimerPhase === "playing") {
                  setIsPodcastPlaying(false);
                }
              }}
            />

            {/* HIDDEN audio element for resource snippets (kept globally) */}
            <audio
              ref={resourcesAudioRef}
              src={resourcesAudioUrl}
              preload="metadata"
              style={{ display: "none" }}
              onPlay={() => setResourceIsPlaying(true)}
              onPause={() => setResourceIsPlaying(false)}
            />


            {podcastConfigLoaded && showAddressEntryFlow && !isPodcastPlaying && (
              <div
                role="dialog"
                aria-modal="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    isMobile && addressEntryMode === "form"
                      ? "#f3f4f6"
                      : "linear-gradient(180deg, #031f35 0%, #04314c 52%, #032c38 100%)",
                  display: "flex",
                  alignItems: isMobile && addressEntryMode === "form" ? "stretch" : "flex-start",
                  justifyContent: isMobile && addressEntryMode === "form" ? "flex-start" : "center",
                  zIndex: 40,
                  overflowY: "auto",
                  padding:
                    isMobile && addressEntryMode === "form"
                      ? 0
                      : isMobile
                      ? "24px 16px"
                      : 24
                }}
              >
                <div
                  style={{
                    width:
                      isMobile && addressEntryMode === "form"
                        ? "100%"
                        : isMobile
                        ? "calc(100% - 32px)"
                        : "min(700px, 82vw)",
                    maxWidth: isMobile && addressEntryMode !== "form" ? 720 : "none",
                    minHeight: isMobile && addressEntryMode === "form" ? "100dvh" : "auto",
                    borderRadius: isMobile && addressEntryMode === "form" ? 0 : (isMobile ? 28 : 30),
                    background: "#f3f4f6",
                    color: "#0f172a",
                    boxShadow: isMobile && addressEntryMode === "form" ? "none" : "0 24px 62px rgba(2, 6, 23, 0.36)",
                    padding:
                      isMobile && addressEntryMode === "form"
                        ? "22px 24px 30px"
                        : isMobile
                        ? "26px 22px 30px"
                        : "40px 48px 42px",
                    overflowY: isMobile && addressEntryMode === "form" ? "auto" : "visible"
                  }}
                >
                  {(addressEntryMode === "loading" || addressEntryMode === "ready") ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          fontSize: 12,
                          letterSpacing: 3,
                          fontWeight: 700,
                          color: "#0f766e"
                        }}
                      >
                        <span style={{ fontSize: 18, lineHeight: 1 }}>🎧</span>
                        <span>
                          {addressEntryMode === "ready"
                            ? (addressFlowText.loadingReadyLabel || "PLAN READY")
                            : (addressFlowText.loadingBuildingLabel || "BUILDING YOUR PLAN")}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: addressEntryMode === "ready" ? 50 : 34,
                          fontWeight: 800,
                          letterSpacing: -1.1,
                          lineHeight: 1.06,
                          maxWidth: addressEntryMode === "loading" ? "9ch" : "none"
                        }}
                      >
                        {addressEntryMode === "ready"
                          ? (addressFlowText.loadingReadyTitle || "All set.")
                          : (addressFlowText.loadingInProgressTitle || "This usually takes a few seconds...")}
                      </div>
                      <div style={{ fontSize: 16, color: "#64748b", marginTop: 2 }}>
                        {isSingleLocation
                          ? "Preparing your audio guide"
                          : (resolvedAddressLabel || searchText || uiText.placeholder)}
                      </div>

                      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 12 }}>
                        {addressProgressSteps.map((stepLabel, idx) => {
                          const visibleStepLabel =
                            isSingleLocation && idx === 0 ? "Loading your location" : stepLabel;
                          const isAudioStep = idx === addressProgressSteps.length - 1;
                          const done = isAudioStep
                            ? autoplayOutcome !== "pending"
                            : addressEntryMode === "ready" || searchLoadingStep > idx;
                          const active = addressEntryMode === "loading" && searchLoadingStep === idx;
                          return (
                            <div
                              key={`${idx}-${visibleStepLabel}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                fontSize: 16,
                                fontWeight: 700
                              }}
                            >
                              <span
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "50%",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background: done ? "#047857" : "transparent",
                                  border: done ? "none" : "2px solid #0f766e",
                                  color: done ? "#ffffff" : active ? "#0f766e" : "#94a3b8",
                                  fontSize: 22,
                                  flexShrink: 0
                                }}
                              >
                                {done ? "✓" : active ? "◌" : "○"}
                              </span>
                              <span style={{ color: done || active ? "#111827" : "#64748b", lineHeight: 1.2 }}>
                                {visibleStepLabel}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {autoplayOutcome === "blocked" && (
                        <div style={{ marginTop: 20, textAlign: "center" }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 14 }}>
                            {addressFlowText.autoplayReadyTitle || "Your evacuation guide is ready!"}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              const media = routesAudioRef.current;
                              if (!media) return;
                              try {
                                await playWithDisclaimer();
                                setShowAddressEntryFlow(false);
                                setAddressEntryMode("form");
                              } catch (error) {
                                setAddressEntryError("Audio could not start. Please try again.");
                              }
                            }}
                            style={{ padding: "14px 34px", border: 0, borderRadius: 14, background: "#f97316", color: "#fff", fontSize: 20, fontWeight: 800, cursor: "pointer" }}
                          >
                            {uiText.play || "Play"}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <img
                        src={WUICastLogo}
                        alt="WUI-Cast"
                        style={{
                          marginTop: isMobile ? 8 : 4,
                          width: isMobile ? 220 : 260,
                          height: "auto",
                          display: "block"
                        }}
                      />

                      <div style={{ marginTop: isMobile ? 10 : 8, color: "#64748b", fontSize: isMobile ? 17 : 19, lineHeight: 1.45 }}>
                        {podcastTitle || addressFlowText.subtitle || "Get your evacuation audio plan"}
                      </div>

                      {!isSingleLocation && (
                        <>
                          <div style={{ marginTop: isMobile ? 22 : 20, position: "relative", zIndex: 3 }}>
                            <div
                              style={{
                                border: isMobile ? "4px solid #0f766e" : "3px solid #0f766e",
                                borderRadius: isMobile ? 28 : 22,
                                background: "#f8fafc",
                                display: "flex",
                                alignItems: "center",
                                padding: isMobile ? "14px 16px" : "14px 18px",
                                gap: 12
                              }}
                            >
                              <span style={{ fontSize: isMobile ? 22 : 24, color: "#0f766e" }}>🔍</span>
                              <input
                                autoFocus
                                type="text"
                                value={searchText}
                                onChange={(e) => {
                                  handleSearchTextInput(e.target.value);
                                  setAddressEntryError("");
                                }}
                                placeholder={uiText.placeholder}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleLocationSearch();
                                }}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  background: "transparent",
                                  fontSize: isMobile ? 17 : 20,
                                  fontWeight: 600,
                                  color: "#0f172a"
                                }}
                              />
                              {!!resolvedAddressLabel && (
                                <span style={{ fontSize: isMobile ? 30 : 26, color: "#0f766e", lineHeight: 1 }}>✓</span>
                              )}
                            </div>

                            {addressSuggestions.length > 0 && (
                              <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 8px)",
                              left: 0,
                              right: 0,
                              zIndex: 10,
                              background: "#e2e8f0",
                              borderRadius: isMobile ? 26 : 18,
                              overflow: "hidden",
                              border: "1px solid #cbd5e1",
                              maxHeight: isMobile ? "min(280px, 42dvh)" : 360,
                              overflowY: "auto",
                              boxShadow: "0 18px 38px rgba(15, 23, 42, 0.18)"
                            }}
                              >
                                {addressSuggestions.map((suggestion, idx) => (
                              <button
                                key={suggestion.id}
                                onClick={() => handleSuggestionPick(suggestion)}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  padding: isMobile ? "14px 16px" : "12px 14px",
                                  border: "none",
                                  borderTop: idx ? "1px solid #cbd5e1" : "none",
                                  background: "transparent",
                                  cursor: "pointer"
                                }}
                              >
                                <div style={{ fontSize: isMobile ? 16 : 17, fontWeight: 700, color: "#0f172a" }}>{suggestion.title}</div>
                                {suggestion.subtitle && (
                                  <div style={{ marginTop: 2, fontSize: isMobile ? 13 : 15, color: "#64748b" }}>{suggestion.subtitle}</div>
                                )}
                              </button>
                                ))}
                              </div>
                            )}

                            {isFetchingSuggestions && (
                              <div style={{ marginTop: 8, fontSize: 14, color: "#64748b", fontWeight: 600 }}>
                                {addressFlowText.searchingAddresses || "Searching addresses..."}
                              </div>
                            )}
                          </div>

                          {resolvedAddressLabel && (
                            <div style={{ marginTop: 10, fontSize: isMobile ? 17 : 18, color: "#0f766e", fontWeight: 700 }}>
                              ✓ {(addressFlowText.addressFoundPrefix || "Address found")} - {resolvedAddressLabel}
                            </div>
                          )}
                        </>
                      )}

                      {addressEntryError && (
                        <div style={{ marginTop: 10, fontSize: 15, color: "#b91c1c", fontWeight: 600 }}>
                          {addressEntryError}
                        </div>
                      )}

                      {isSingleLocation && hasSimulationDataFailure && (
                        <div style={{ marginTop: 10, fontSize: 15, color: "#b91c1c", fontWeight: 600 }}>
                          We couldn&apos;t load the simulation data. Check your connection and try again.
                        </div>
                      )}

                      <button
                        onClick={isSingleLocation
                          ? (hasSimulationDataFailure ? retrySimulationData : handleSingleLocationStart)
                          : () => handleLocationSearch()}
                        disabled={
                          isSearchingLoc ||
                          (isSingleLocation ? singleLocationCtaDisabled : !searchText.trim())
                        }
                        style={{
                          marginTop: 16,
                          width: "100%",
                          background:
                            (isSingleLocation ? singleLocationCtaDisabled : !searchText.trim())
                              ? "#9ca3af"
                              : "#047857",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: isMobile ? 24 : 18,
                          padding: isMobile ? "18px 14px" : "16px 14px",
                          fontSize: isMobile ? 16 : 18,
                          fontWeight: 800,
                          cursor:
                            isSearchingLoc || (isSingleLocation ? singleLocationCtaDisabled : !searchText.trim())
                              ? "default"
                              : "pointer",
                          opacity:
                            isSearchingLoc || (isSingleLocation ? singleLocationCtaDisabled : !searchText.trim())
                              ? 0.75
                              : 1
                        }}
                      >
                        {isSingleLocation
                          ? hasSimulationDataFailure
                            ? isSimulationDataLoading ? "Retrying…" : "Retry loading results"
                            : (podcastCta || addressFlowText.startAudioGuide || "Start audio guide")
                          : uiText.findPlan}
                      </button>

                      <div
                        style={{
                          marginTop: isMobile ? 18 : 20,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                          padding: isMobile ? "14px 16px" : "16px 18px",
                          borderRadius: isMobile ? 18 : 16,
                          background: "#eef2f7",
                          border: "1px solid #d9e2ec",
                          color: "#0f172a",
                          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
                        }}
                      >
                        <div
                          style={{
                            fontSize: isMobile ? 14 : 15,
                            lineHeight: 1.42,
                            fontWeight: 650
                          }}
                        >
                          {(
                            Array.isArray(addressFlowText.entryDisclaimerLines) && addressFlowText.entryDisclaimerLines.length
                              ? addressFlowText.entryDisclaimerLines
                              : [
                                  addressFlowText.entryDisclaimer ||
                                    "In an actual emergency, always follow official instructions from law enforcement, fire officials, and local emergency authorities."
                                ]
                          ).map((line, idx) => (
                            <div key={`${line}-${idx}`} style={{ marginTop: idx ? 12 : 0 }}>
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>

                      {partnerLogoSections.length > 0 && (
                        <div
                          style={{
                            marginTop: isMobile ? 28 : 34,
                            paddingTop: isMobile ? 18 : 22,
                            borderTop: "1px solid #cbd5e1",
                            display: "grid",
                            gap: isMobile ? 22 : 28
                          }}
                        >
                          {partnerLogoSections.map((section) => {
                            const isPrimaryTier = Number(section.rank) === 1;
                            return (
                              <div key={section.key}>
                              <div
                                style={{
                                  fontSize: isMobile ? 13 : 15,
                                  letterSpacing: 4,
                                  fontWeight: 800,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  textAlign: "center"
                                }}
                              >
                                {section.name}
                              </div>
                              <div
                                style={{
                                  marginTop: isMobile ? 14 : 18,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: isPrimaryTier
                                    ? (isMobile ? "20px 38px" : "28px 96px")
                                    : (isMobile ? "18px 34px" : "24px 72px"),
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                {section.logos.map((logo, idx) => (
                                  <div
                                    key={`${logo?.storagePath || logo?.url || section.key}-${idx}`}
                                    style={{
                                      minHeight: isPrimaryTier
                                        ? (isMobile ? 82 : 118)
                                        : (isMobile ? 62 : 78),
                                      flex: isMobile
                                        ? (isPrimaryTier ? "0 1 calc(50% - 20px)" : "0 1 calc(50% - 18px)")
                                        : (isPrimaryTier ? "0 1 190px" : "0 1 150px"),
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center"
                                    }}
                                  >
                                    <img
                                      src={logo.url}
                                      alt={logo?.name || `${section.name} logo`}
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: isPrimaryTier
                                          ? (isMobile ? 76 : 108)
                                          : (isMobile ? 58 : 76),
                                        objectFit: "contain",
                                        display: "block"
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}

                    </>
                  )}
                </div>
              </div>
            )}

            {/* NEXT STEPS MODAL */}
            {!demoGateEnabled && showNextSteps && (
              <NextStepsModal
                isMobile={isMobile}
                uiText={uiText}
                activeStepsTab={activeStepsTab}
                setActiveStepsTab={setActiveStepsTab}
                onClose={() => setShowNextSteps(false)}
                onReplay={handlePlayAgain}
                showEvacuationTimesTab={INTERACTIVE_MAP_FEATURES.showEvacuationTimesTab}
                enableVoiceAssistant={ENABLE_NEXT_STEPS_VOICE_ASSISTANT}
                nextStepsVoiceAssistant={nextStepsVoiceAssistant}
                guidedNextSteps={guidedNextSteps}
                activeNextStep={activeNextStep}
                activeNextStepIndex={activeNextStepIndex}
                setActiveNextStepIndex={setActiveNextStepIndex}
                completedNextStepSet={completedNextStepSet}
                hiddenNextStepKeys={hiddenNextStepKeys}
                setOpenInstructionKey={setOpenInstructionKey}
                stopResourcePlayback={stopResourcePlayback}
                toggleGuidedStepDetails={toggleGuidedStepDetails}
                isPlayableSegment={isPlayableSegment}
                segmentDuration={segmentDuration}
                resourceProgress={resourceProgress}
                activeResourceKey={activeResourceKey}
                resourceIsPlaying={resourceIsPlaying}
                playResourceSegment={playResourceSegment}
                seekWithinResourceSegment={seekWithinResourceSegment}
                openResourceAndPlay={openResourceAndPlay}
                markNextStepComplete={markNextStepComplete}
                skipActiveGuidedStep={skipActiveGuidedStep}
                resourceUI={resourceUI}
                shareWithNeighbors={shareWithNeighbors}
                faqList={faqList}
                simulationPdfUrl={simulationPdfUrl}
                routeTableRows={routeTableRows}
                routeTableLoading={routeTableLoading}
                routeTableError={routeTableError}
                nearestNodeId={nearestNodeIdRef.current}
                onPdfAnalytics={logSimulationPdfAnalytics}
                onSendAssistantStepContext={nextStepsVoiceAssistant.sendStepContext}
              />
            )}
            {demoPrompt && (
              <DemoLeadGateModal
                mode={demoPrompt}
                playbackTime={demoPromptTime}
                podcastId={podcastId}
                onContinue={resumeDemoPlayback}
                onSubmitted={completeDemoSubmission}
              />
            )}
            </div>
    </div>
  );
};

export default InteractiveMapMain;
